<!-- eslint-disable vue/no-v-for-template-key -->
<template>
  <div class="fleet-details-page">
    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" @click="$router.go(-1)" title="Wstecz">
        <v-icon size="18">mdi-arrow-left</v-icon>
      </button>
      <div class="page-avatar">
        <v-icon size="22" color="#fff">mdi-car</v-icon>
      </div>
      <div class="page-header-text">
        <h1 class="page-title">Szczegóły pojazdu</h1>
      </div>
    </div>
    <v-dialog v-model="showDialog" max-width="1200" persistent>
      <v-card>
        <v-card-title class="text-h4 primary--text font-weight-bold text-center py-6">
          <v-icon left large color="primary">mdi-compare</v-icon>
          {{ $t('fleetDetails.protocolComparison') }}
          <v-spacer></v-spacer>
          <v-chip color="success" text-color="white" class="mr-2">
            <v-icon left small>mdi-check</v-icon>
            Zgodne: {{ getMatchingItemsCount() }}
          </v-chip>
          <v-chip color="error" text-color="white">
            <v-icon left small>mdi-close</v-icon>
            Różne: {{ getDifferentItemsCount() }}
          </v-chip>
        </v-card-title>

        <v-card-subtitle class="text-center pb-4">
          <v-chip color="success" outlined class="mr-2">
            <v-icon left small>mdi-calendar-start</v-icon>
            Pierwszy: {{ comparisonInfo.first && comparisonInfo.first.date ? comparisonInfo.first.date.substring(0, 16) : 'N/A' }}
          </v-chip>
          <v-icon>mdi-compare-horizontal</v-icon>
          <v-chip color="error" outlined class="ml-2">
            <v-icon left small>mdi-calendar-end</v-icon>
            Ostatni: {{ comparisonInfo.final && comparisonInfo.final.date ? comparisonInfo.final.date.substring(0, 16) : 'N/A' }}
          </v-chip>
        </v-card-subtitle>

        <v-card-text>
          <!-- Legend -->
          <v-row class="mb-4">
            <v-col cols="12">
              <v-card outlined class="pa-3">
                <v-card-subtitle class="font-weight-bold">Legenda:</v-card-subtitle>
                <v-row dense>
                  <v-col cols="4">
                    <v-chip small color="success" text-color="white" class="mr-2">
                      <v-icon left x-small>mdi-check</v-icon>
                      {{ $t('fleetDetails.matching') }}
                    </v-chip>
                  </v-col>
                  <v-col cols="4">
                    <v-chip small color="error" text-color="white" class="mr-2">
                      <v-icon left x-small>mdi-close</v-icon>
                      {{ $t('fleetDetails.different') }}
                    </v-chip>
                  </v-col>
                  <v-col cols="4">
                    <v-chip small color="warning" text-color="black">
                      <v-icon left x-small>mdi-help</v-icon>
                      {{ $t('fleetDetails.missingInOther') }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <!-- SUPER PROSTE PORÓWNANIE -->
          <v-row>
            <v-col cols="12">
              <!-- Jeśli wszystko OK -->
              <div v-if="getSimpleChanges().length === 0">
                <v-alert type="success" class="text-center ma-4" style="font-size: 18px;">
                  <v-icon size="60" class="mb-2">mdi-check-circle</v-icon>
                  <div class="text-h5 font-weight-bold mb-2">{{ $t('fleetDetails.allOk') }}</div>
                  <div class="text-h6">{{ $t('fleetDetails.sameState') }}</div>
                </v-alert>
              </div>

              <!-- Jeśli są zmiany -->
              <div v-else>
                <v-alert color="orange" dark class="mb-4" style="font-size: 16px;">
                  <v-icon left size="30">mdi-alert</v-icon>
                  <span class="text-h6">{{ $t('fleetDetails.changesFound', { count: getSimpleChanges().length }) }}</span>
                </v-alert>

                <!-- Lista zmian -->
                <v-card
                  v-for="(change, index) in getSimpleChanges()"
                  :key="index"
                  class="mb-3"
                  elevation="4"
                  :color="change.isGood ? 'light-green lighten-5' : 'red lighten-5'"
                >
                  <v-card-text class="pa-4">
                    <v-row align="center">
                      <!-- Co się zmieniło -->
                      <v-col cols="12" md="4">
                        <div class="text-h6 font-weight-bold" :class="change.isGood ? 'green--text text--darken-2' : 'red--text text--darken-2'">
                          {{ change.what }}
                        </div>
                      </v-col>

                      <!-- BYŁY -->
                      <v-col cols="6" md="4">
                        <div class="comparison-side">
                          <div class="text-subtitle-2 grey--text text--darken-1 mb-1">{{ $t('fleetDetails.before') }}</div>
                          <!-- Dla uwag pokazuj tekst -->
                          <div v-if="change.what.includes('UWAGI')" class="description-text before-text">
                            {{ change.before }}
                          </div>
                          <!-- Dla reszty pokazuj chip -->
                          <v-chip
                            v-else
                            large
                            :color="change.beforeGood ? 'green' : 'grey'"
                            :text-color="'white'"
                            class="font-weight-bold"
                          >
                            {{ change.before }}
                          </v-chip>
                        </div>
                      </v-col>

                      <!-- JEST -->
                      <v-col cols="6" md="4">
                        <div class="comparison-side">
                          <div class="text-subtitle-2 grey--text text--darken-1 mb-1">{{ $t('fleetDetails.after') }}</div>
                          <!-- Dla uwag pokazuj tekst -->
                          <div v-if="change.what.includes('UWAGI')" class="description-text after-text">
                            {{ change.after }}
                            <v-icon
                              color="info"
                              size="20"
                              class="ml-2"
                            >
                              mdi-information
                            </v-icon>
                          </div>
                          <!-- Dla reszty pokazuj chip z ikoną -->
                          <div v-else class="d-flex align-center">
                            <v-chip
                              large
                              :color="change.afterGood ? 'green' : 'red'"
                              :text-color="'white'"
                              class="font-weight-bold"
                            >
                              {{ change.after }}
                            </v-chip>
                            <v-icon
                              :color="change.isGood ? 'green' : 'red'"
                              size="30"
                              class="ml-2"
                            >
                              {{ change.isGood ? 'mdi-trending-up' : 'mdi-trending-down' }}
                            </v-icon>
                          </div>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Wyjaśnienie -->
                    <v-row v-if="change.explanation">
                      <v-col cols="12">
                        <v-alert
                          :type="change.isGood ? 'success' : 'error'"
                          dense
                          class="mt-2 mb-0"
                          style="font-size: 14px;"
                        >
                          {{ change.explanation }}
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <!-- Podsumowanie -->
                <v-card color="blue-grey lighten-4" class="mt-4">
                  <v-card-text class="text-center pa-4">
                    <v-icon size="40" color="blue-grey" class="mb-2">mdi-information-outline</v-icon>
                    <div class="text-h6 mb-2">{{ $t('fleetDetails.howToRead') }}</div>
                    <div class="text-body-1">
                      🟢 <strong>{{ $t('fleetDetails.greenLabel') }}</strong> {{ $t('fleetDetails.greenMeans') }}<br>
                      🔴 <strong>{{ $t('fleetDetails.redLabel') }}</strong> {{ $t('fleetDetails.redMeans') }}
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            large
            color="primary"
            @click="
              ((showDialog = false),
              (selectedTables = []),
              (comparedTables = []),
              (comparisonInfo = { first: {}, final: {} }))
            "
            >
            <v-icon left>mdi-close</v-icon>
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Layout with Sidebar -->
    <v-row>
      <v-col cols="12" md="3">
        <!-- Left Sidebar with Car Management -->
        <v-card elevation="2" class="mb-4">
          <v-card-title class="primary white--text">
            <v-icon left>mdi-car-cog</v-icon>
            {{ $t('fleetDetails.carManagement') }}
          </v-card-title>
          <v-list>
            <v-list-item v-if="canAccess('licenses')" @click="tab = 0; dialog = true">
              <v-list-item-icon>
                <v-icon color="primary">mdi-license</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('fleetDetails.licenses') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canAccess('rent-protocol')" @click="tab = 1; dialog = true">
              <v-list-item-icon>
                <v-icon color="success">mdi-car-key</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('fleetDetails.rentProtocol') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canAccess('end-rent')" @click="tab = 2; dialog = true">
              <v-list-item-icon>
                <v-icon color="error">mdi-car-off</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('fleetDetails.endRent') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canAccess('service')" @click="tab = 3; dialog = true">
              <v-list-item-icon>
                <v-icon color="info">mdi-car-cog</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('fleetDetails.service') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canAccess('crash')" @click="tab = 4; dialog = true">
              <v-list-item-icon>
                <v-icon color="orange">mdi-tow-truck</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('fleetDetails.crash') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>

      </v-col>

      <v-col cols="12" md="9">
        <!-- Main Content Area -->

    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title class="text-h4 primary--text font-weight-bold text-center py-6">
          <v-icon left large color="primary">mdi-history</v-icon>
          {{ $t('fleetDetails.carHistoryManagement') }}
        </v-card-title>

        <v-card-text>

          <div v-show="tab === 0">
            <!-- Licencje Tab -->
              <v-row dense>
              <v-col cols="12">
    <v-card>
      <v-card-title class="text-h5 primary--text font-weight-bold">
        <v-icon left color="primary">mdi-license</v-icon>
        {{ $t('fleetDetails.assignLicenses') }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- License Number Input -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newLicense.licenseNo"
                :label="$t('fleetDetails.licenseNumber')"
                :rules="[rules.required]"
                required
              ></v-text-field>
            </v-col>

            <!-- License Document Number Input -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newLicense.text"
                :label="$t('fleetDetails.licenseExcerptNumber')"
                :rules="[rules.required]"
                required
              ></v-text-field>
            </v-col>

            <!-- City Input -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="newLicense.city"
                :label="$t('fleetDetails.city')"
                :rules="[rules.required]"
                required
              ></v-text-field>
            </v-col>

            <!-- Save Button -->
            <v-col cols="12">
              <v-btn
                color="primary"
                block
                large
                @click="saveLicenses"
                :disabled="!valid"
                class="text-h6 font-weight-bold"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ $t('fleetDetails.saveLicense') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <!-- Table of Saved Licenses -->
        <v-simple-table class="mt-4" dense>
          <thead>
            <tr>
              <th class="text-left">{{ $t('fleetDetails.licenseNumber') }}</th>
              <th class="text-left">{{ $t('fleetDetails.licenseExcerptNumber') }}</th>
              <th class="text-left">{{ $t('fleet.branch') }}</th>
              <th class="text-left">{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(license, index) in licenses" :key="index">
              <td>{{ license.licenseNo }}</td>
              <td>{{ license.text }}</td>
              <td>{{ license.city }}</td>
              <td>
                <v-btn
                  icon
                  color="red"
                  @click="deleteLicense(license.id)"
                  :title="$t('common.delete')"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="licenses.length === 0">
              <td colspan="4" class="text-center">{{ $t('fleetDetails.noLicenses') }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>
              </v-col>
              </v-row>
          </div>

          <div v-show="tab === 1">
            <!-- Protokół Najmu Tab -->
              <v-card class="ma-2" elevation="2">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-car-key</v-icon>
                  {{ $t('fleetDetails.rentProtocol') }}
                </v-card-title>
                <v-card-text>
                  <v-row dense>
                  <v-col cols="12">
                <v-autocomplete
                  v-model="rent.driverId"
                  :items="drivers"
                  :label="$t('common.driver')"
                  outlined
                  required
                  clearable
                  item-text="name"
                  item-value="id"
                  :no-data-text="$t('common.noResults') || 'Brak wyników'"
                  prepend-inner-icon="mdi-account-search"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="rent.startDate"
                  label="Data rozp."
                  type="date"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <!-- Godzina wydania: mandaty i odpowiedzialność za auto. Dzień
                     wydania płaci biorący bez względu na godzinę. -->
                <v-text-field
                  v-model="rent.startTime"
                  label="Godzina wydania"
                  type="time"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="rent.price"
                  label="Pobrana Kaucja"
                  type="number"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="rent.paymentMethod"
                  :items="paymentMethods"
                  label="Metoda Pobrania Kaucji"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="rent.startMileage"
                  label="Przebieg przy wydaniu (km)"
                  type="number"
                  outlined
                  required
                  prepend-icon="mdi-speedometer"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <!-- Zdjęcia dokładamy partiami, jak przy protokole zdania auta:
                     v-file-input przy każdym wyborze PODMIENIAŁ listę, więc nie
                     dało się dorobić kolejnych zdjęć w drugim podejściu. -->
                <div class="mb-1" style="font-size:13px;color:rgba(0,0,0,.6)">{{ $t('fleet.carPhotos') }}</div>
                <input ref="rentPhotoInput" type="file" accept="image/*" multiple style="display:none" @change="onRentPhotos" />
                <v-btn small outlined color="primary" @click="$refs.rentPhotoInput.click()">
                  <v-icon left small>mdi-camera-plus</v-icon>Dodaj zdjęcia
                </v-btn>
                <span v-if="rent.photos && rent.photos.length" class="ml-3 caption grey--text">{{ rent.photos.length }} zdj.</span>
                <div v-if="rent.photos && rent.photos.length" class="d-flex flex-wrap mt-2" style="gap:8px">
                  <div v-for="(f, i) in rent.photos" :key="i" style="position:relative">
                    <img v-if="rentPhotoPreviews[i]" :src="rentPhotoPreviews[i]" style="width:76px;height:76px;object-fit:cover;border-radius:8px;border:1px solid #ddd" />
                    <v-btn icon x-small color="error" style="position:absolute;top:-8px;right:-8px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.3)" @click="removeRentPhoto(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-divider class="my-6"></v-divider>
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="primary">mdi-clipboard-check</v-icon>
                    Protokół Przeglądu Pojazdu
                  </v-card-title>
                  <v-card-text>
                    <!-- Poziom paliwa - warunkowe na rodzaj paliwa -->
                    <v-card class="mb-6 protocol-section" outlined elevation="3">
                      <v-card-subtitle class="font-weight-bold text-h6 pb-2 protocol-section-header">
                        <v-icon left color="amber" size="24">mdi-gas-station</v-icon>
                        Poziom paliwa
                        <v-spacer></v-spacer>
                        <v-chip small outlined>{{ carFuelType === 'DIESEL' ? 'Diesel' : carFuelType === 'BENZYNA_LPG' ? 'Benzyna + LPG' : 'Benzyna' }}</v-chip>
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pt-4">
                        <v-row dense>
                          <!-- Diesel -->
                          <v-col v-if="carFuelType === 'DIESEL'" cols="12" sm="6" md="4">
                            <v-select
                              v-model="fuelLevelDiesel"
                              :items="fuelLevelScaleOptions"
                              label="Poziom diesla"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <!-- Benzyna -->
                          <v-col v-if="carFuelType === 'BENZYNA'" cols="12" sm="6" md="4">
                            <v-select
                              v-model="fuelLevelPetrol"
                              :items="fuelLevelScaleOptions"
                              label="Poziom benzyny"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <!-- Benzyna + LPG -->
                          <template v-if="carFuelType === 'BENZYNA_LPG'">
                            <v-col cols="12" sm="6" md="4">
                              <v-select
                                v-model="fuelLevelPetrol"
                                :items="fuelLevelScaleOptions"
                                label="Poziom benzyny"
                                outlined
                                dense
                              ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <v-select
                                v-model="fuelLevelLPG"
                                :items="fuelLevelScaleOptions"
                                label="Poziom LPG"
                                outlined
                                dense
                              ></v-select>
                            </v-col>
                          </template>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <!-- Wyposażenie i dokumenty -->
                    <v-card class="mb-6 protocol-section" outlined elevation="3">
                      <v-card-subtitle class="font-weight-bold text-h6 pb-2 protocol-section-header">
                        <v-icon left color="blue" size="24">mdi-briefcase</v-icon>
                        Wyposażenie i dokumenty
                        <v-spacer></v-spacer>
                        <v-chip small color="blue" text-color="white">{{ protocol.slice(5, 19).filter(item => item.checked).length }}/14</v-chip>
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pt-4">
                        <v-row dense>
                          <v-col cols="12" sm="6" md="4" lg="3" v-for="item in protocol.slice(5, 19)" :key="item.id">
                            <v-checkbox
                              v-model="item.checked"
                              :label="item.label"
                              class="ma-0 pa-1 protocol-checkbox"
                              dense
                              hide-details
                              color="blue"
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <!-- Stan techniczny pojazdu -->
                    <v-card class="mb-6 protocol-section" outlined elevation="3">
                      <v-card-subtitle class="font-weight-bold text-h6 pb-2 protocol-section-header">
                        <v-icon left color="green" size="24">mdi-car-wrench</v-icon>
                        Stan techniczny pojazdu
                        <v-spacer></v-spacer>
                        <v-chip small color="green" text-color="white">{{ protocol.slice(19).filter(item => item.checked).length }}/9</v-chip>
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pt-4">
                        <v-row dense>
                          <v-col cols="12" sm="6" md="4" lg="3" v-for="item in protocol.slice(19)" :key="item.id">
                            <v-checkbox
                              v-model="item.checked"
                              :label="item.label"
                              class="ma-0 pa-1 protocol-checkbox"
                              dense
                              hide-details
                              color="green"
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-card-text>
                </v-card>
              </v-col>
                            <v-col cols="12">
                <v-textarea
                  v-model="rent.description"
                  label="Uwagi"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="primary">mdi-signature-freehand</v-icon>
                    Podpis kierowcy
                  </v-card-title>
                  <v-card-text>
                    <div class="signature-container">
                      <SignatureField
                        width="100%"
                        height="200px"
                        ref="signaturePad"
                        class="signature-pad elevation-1"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="secondary">mdi-signature-freehand</v-icon>
                    Podpis pracownika
                  </v-card-title>
                  <v-card-text>
                    <div class="signature-container">
                      <SignatureField
                        width="100%"
                        height="200px"
                        ref="workerSignaturePad"
                        class="signature-pad elevation-1"
                      />
                    </div>
                    <div class="d-flex align-center mt-2" v-if="myCanUseFacsimile && myFacsimile">
                      <v-btn small outlined color="primary" @click="useWorkerFacsimile('start')">
                        <v-icon left small>mdi-draw</v-icon>Wstaw moją faksymilkę
                      </v-btn>
                      <template v-if="workerFacsimileStart">
                        <img :src="myFacsimile" style="max-height:40px; margin-left:12px; background:#fff; border:1px solid #ddd; border-radius:4px;" />
                        <span class="caption green--text ml-2">Faksymilka wstawiona</span>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-btn
                  block
                  large
                  color="primary"
                  @click="saveStartRent()"
                  :disabled="driversEndRent.length === 2"
                  class="text-h6 font-weight-bold"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ $t('fleetDetails.saveRentProtocol') }}
                </v-btn>
                  </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
          </div>

          <div v-show="tab === 2">
            <!-- Protokół Zakończenia Najmu Tab -->
              <v-card class="ma-2" elevation="2">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-car-off</v-icon>
                  {{ $t('fleetDetails.endRentProtocol') }}
                </v-card-title>
                <v-card-text>
                  <v-row dense>
              <v-col cols="12">
                <v-select
                  v-model="endRent.driverId"
                  :items="driversEndRent"
                  label="Kierowca"
                  outlined
                  required
                  item-text="name"
                  item-value="id"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="endRent.endDate"
                  label="Data zakończenia"
                  type="date"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <!-- Zwrot po 12:00 = oddający płaci jeszcze za ten dzień. -->
                <v-text-field
                  v-model="endRent.endTime"
                  label="Godzina zwrotu"
                  type="time"
                  hint="Po 12:00 dzień zwrotu jest płatny"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="endRent.endMileage"
                  label="Przebieg przy zwrocie (km)"
                  type="number"
                  outlined
                  required
                  prepend-icon="mdi-speedometer"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="mb-1" style="font-size:13px;color:rgba(0,0,0,.6)">Zdjęcia auta</div>
                <input ref="endRentPhotoInput" type="file" accept="image/*" multiple style="display:none" @change="onEndRentPhotos" />
                <v-btn small outlined color="primary" @click="$refs.endRentPhotoInput.click()">
                  <v-icon left small>mdi-camera-plus</v-icon>Dodaj zdjęcia
                </v-btn>
                <span v-if="endRent.photos && endRent.photos.length" class="ml-3 caption grey--text">{{ endRent.photos.length }} zdj.</span>
                <div v-if="endRent.photos && endRent.photos.length" class="d-flex flex-wrap mt-2" style="gap:8px">
                  <div v-for="(f, i) in endRent.photos" :key="i" style="position:relative">
                    <img v-if="endRentPhotoPreviews[i]" :src="endRentPhotoPreviews[i]" style="width:76px;height:76px;object-fit:cover;border-radius:8px;border:1px solid #ddd" />
                    <v-btn icon x-small color="error" style="position:absolute;top:-8px;right:-8px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.3)" @click="removeEndRentPhoto(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="endRent.description"
                  label="Uwagi"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="primary">mdi-clipboard-check</v-icon>
                    Protokół Przeglądu Pojazdu
                  </v-card-title>
                  <v-card-text>
                    <!-- Poziom paliwa - warunkowe na rodzaj paliwa -->
                    <v-card class="mb-6 protocol-section" outlined elevation="3">
                      <v-card-subtitle class="font-weight-bold text-h6 pb-2 protocol-section-header">
                        <v-icon left color="amber" size="24">mdi-gas-station</v-icon>
                        Poziom paliwa
                        <v-spacer></v-spacer>
                        <v-chip small outlined>{{ carFuelType === 'DIESEL' ? 'Diesel' : carFuelType === 'BENZYNA_LPG' ? 'Benzyna + LPG' : 'Benzyna' }}</v-chip>
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pt-4">
                        <v-row dense>
                          <!-- Diesel -->
                          <v-col v-if="carFuelType === 'DIESEL'" cols="12" sm="6" md="4">
                            <v-select
                              v-model="fuelLevelDiesel"
                              :items="fuelLevelScaleOptions"
                              label="Poziom diesla"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <!-- Benzyna -->
                          <v-col v-if="carFuelType === 'BENZYNA'" cols="12" sm="6" md="4">
                            <v-select
                              v-model="fuelLevelPetrol"
                              :items="fuelLevelScaleOptions"
                              label="Poziom benzyny"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <!-- Benzyna + LPG -->
                          <template v-if="carFuelType === 'BENZYNA_LPG'">
                            <v-col cols="12" sm="6" md="4">
                              <v-select
                                v-model="fuelLevelPetrol"
                                :items="fuelLevelScaleOptions"
                                label="Poziom benzyny"
                                outlined
                                dense
                              ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <v-select
                                v-model="fuelLevelLPG"
                                :items="fuelLevelScaleOptions"
                                label="Poziom LPG"
                                outlined
                                dense
                              ></v-select>
                            </v-col>
                          </template>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <!-- Wyposażenie i dokumenty -->
                    <v-card class="mb-6 protocol-section" outlined elevation="3">
                      <v-card-subtitle class="font-weight-bold text-h6 pb-2 protocol-section-header">
                        <v-icon left color="blue" size="24">mdi-briefcase</v-icon>
                        Wyposażenie i dokumenty
                        <v-spacer></v-spacer>
                        <v-chip small color="blue" text-color="white">{{ protocol.slice(5, 19).filter(item => item.checked).length }}/14</v-chip>
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pt-4">
                        <v-row dense>
                          <v-col cols="12" sm="6" md="4" lg="3" v-for="item in protocol.slice(5, 19)" :key="item.id">
                            <v-checkbox
                              v-model="item.checked"
                              :label="item.label"
                              class="ma-0 pa-1 protocol-checkbox"
                              dense
                              hide-details
                              color="blue"
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <!-- Stan techniczny pojazdu -->
                    <v-card class="mb-6 protocol-section" outlined elevation="3">
                      <v-card-subtitle class="font-weight-bold text-h6 pb-2 protocol-section-header">
                        <v-icon left color="green" size="24">mdi-car-wrench</v-icon>
                        Stan techniczny pojazdu
                        <v-spacer></v-spacer>
                        <v-chip small color="green" text-color="white">{{ protocol.slice(19).filter(item => item.checked).length }}/9</v-chip>
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pt-4">
                        <v-row dense>
                          <v-col cols="12" sm="6" md="4" lg="3" v-for="item in protocol.slice(19)" :key="item.id">
                            <v-checkbox
                              v-model="item.checked"
                              :label="item.label"
                              class="ma-0 pa-1 protocol-checkbox"
                              dense
                              hide-details
                              color="green"
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="primary">mdi-signature-freehand</v-icon>
                    Podpis kierowcy
                  </v-card-title>
                  <v-card-text>
                    <div class="signature-container">
                      <SignatureField
                        width="100%"
                        height="200px"
                        ref="endRentSignaturePad"
                        class="signature-pad elevation-1"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card class="mb-4" elevation="2">
                  <v-card-title class="text-h6 primary--text font-weight-bold">
                    <v-icon left color="secondary">mdi-signature-freehand</v-icon>
                    Podpis pracownika
                  </v-card-title>
                  <v-card-text>
                    <div class="signature-container">
                      <SignatureField
                        width="100%"
                        height="200px"
                        ref="endRentWorkerSignaturePad"
                        class="signature-pad elevation-1"
                      />
                    </div>
                    <div class="d-flex align-center mt-2" v-if="myCanUseFacsimile && myFacsimile">
                      <v-btn small outlined color="primary" @click="useWorkerFacsimile('end')">
                        <v-icon left small>mdi-draw</v-icon>Wstaw moją faksymilkę
                      </v-btn>
                      <template v-if="workerFacsimileEnd">
                        <img :src="myFacsimile" style="max-height:40px; margin-left:12px; background:#fff; border:1px solid #ddd; border-radius:4px;" />
                        <span class="caption green--text ml-2">Faksymilka wstawiona</span>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-btn
                  block
                  large
                  color="primary"
                  @click="saveEndRent()"
                  class="text-h6 font-weight-bold"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ $t('fleetDetails.saveEndProtocol') }}
                </v-btn>
                  </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
          </div>

          <div v-show="tab === 3">
            <!-- Serwis Tab -->
              <v-card class="ma-2" elevation="2">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-car-cog</v-icon>
                  {{ $t('fleetDetails.service') }}
                </v-card-title>
                <v-card-text>
                  <v-row dense>
                            <v-col cols="12">
                <v-select
                  v-model="service.driverId"
                  :items="driversEndRent"
                  label="Kierowca"
                  outlined
                  required
                  item-text="name"
                  item-value="id"
                />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="service.photos"
                  label="Dokumentacja Serwisu"
                  multiple
                ></v-file-input>
              </v-col>
                           <v-col cols="12">
                <v-textarea
                  v-model="service.description"
                  label="Uwagi"
                ></v-textarea>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="service.date"
                  label="Data Serwisu"
                  type="date"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="service.odometer"
                  label="Przebieg"
                  type="number"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-btn
                  block
                  large
                  color="primary"
                  @click="saveService()"
                  class="text-h6 font-weight-bold"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ $t('fleetDetails.saveService') }}
                </v-btn>
                  </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
          </div>

          <div v-show="tab === 4">
            <!-- Szkoda Tab -->
              <v-card class="ma-2" elevation="2">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-tow-truck</v-icon>
                  {{ $t('fleetDetails.crash') }}
                </v-card-title>
                <v-card-text>
                  <v-row dense>
                                          <v-col cols="12">
                <v-select
                  v-model="crash.driverId"
                  :items="driversEndRent"
                  label="Kierowca"
                  outlined
                  required
                  item-text="name"
                  item-value="id"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="crash.date"
                  label="Data szkody"
                  type="date"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-file-input
                  v-model="crash.photos"
                  label="Zdjęcia szkody"
                  multiple
                ></v-file-input>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="crash.insuranceInfo"
                  label="Dane ubezpieczyciela"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="crash.incidentNumber"
                  label="Numer Szkody"
                ></v-text-field>
              </v-col>
                                         <v-col cols="12">
                <v-textarea
                  v-model="crash.description"
                  label="Uwagi"
                ></v-textarea>
              </v-col>
                          <v-col cols="12">
                <v-btn
                  block
                  large
                  color="primary"
                  @click="saveCrash()"
                  class="text-h6 font-weight-bold"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ $t('fleetDetails.saveCrash') }}
                </v-btn>
                  </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            block
            large
            color="error"
            @click="dialog = false"
            class="text-h6 font-weight-bold"
          >
            <v-icon left>mdi-close</v-icon>
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-divider class="my-8"></v-divider>
    <v-row class="mb-6">
      <v-col cols="12">
        <v-text-field
          v-model="search"
          :label="$t('fleetDetails.searchHistory')"
          clearable
          @change="initialize()"
          outlined
          prepend-icon="mdi-magnify"
          class="search-field"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Inspekcje pojazdu — self-inspekcje kierowców i inspekcje admina, ze zdjęciami -->
    <v-card elevation="4" class="mb-8">
      <v-card-title class="text-h5 primary--text font-weight-bold">
        <v-icon left color="primary">mdi-clipboard-check-outline</v-icon>
        Inspekcje pojazdu
        <v-chip v-if="carInspections.length" x-small class="ml-3" color="primary" outlined>{{ carInspections.length }}</v-chip>
        <v-spacer></v-spacer>
        <v-progress-circular v-if="inspLoading" indeterminate size="20" width="2" color="primary"></v-progress-circular>
      </v-card-title>
      <v-card-text>
        <div v-if="!inspLoading && !carInspections.length" class="grey--text">Brak inspekcji tego auta.</div>
        <v-card v-for="insp in carInspections" :key="insp.id" outlined class="mb-3 pa-3">
          <div class="d-flex align-center flex-wrap" style="gap:6px;">
            <v-chip x-small :color="inspStatusColor(insp.status)" text-color="white">{{ inspStatusLabel(insp.status) }}</v-chip>
            <span class="font-weight-medium">{{ formatInspDateTime(insp.createdAt) }}</span>
            <span class="grey--text" v-if="insp.driver">· {{ insp.driver.firstName }} {{ insp.driver.lastName }} (kierowca)</span>
            <span class="grey--text" v-else-if="insp.admin">· {{ insp.admin.firstName }} {{ insp.admin.lastName }} (admin)</span>
            <v-spacer></v-spacer>
            <template v-if="insp.status === 'PENDING_REVIEW'">
              <v-btn x-small color="success" :loading="inspReviewingId === insp.id" @click="reviewCarInspection(insp.id, 'ACCEPT')" class="mr-1">Akceptuj</v-btn>
              <v-btn x-small color="error" :loading="inspReviewingId === insp.id" @click="reviewCarInspection(insp.id, 'FLAG')">Oflaguj</v-btn>
            </template>
          </div>
          <div v-if="inspMileage(insp)" class="mt-1 caption">Przebieg: {{ inspMileage(insp) }} km</div>
          <div v-if="inspRatings(insp).length" class="mt-2 d-flex" style="gap:6px; flex-wrap:wrap;">
            <v-chip v-for="(r, i) in inspRatings(insp)" :key="i" x-small outlined :color="r.color">{{ r.name }}: <b class="ml-1">{{ r.label }}</b></v-chip>
          </div>
          <div v-if="insp.note" class="mt-1 caption">Uwagi kierowcy: {{ insp.note }}</div>
          <div v-if="insp.reviewNote" class="mt-1 caption">Uwagi admina: {{ insp.reviewNote }}</div>
          <div v-if="insp.photos && insp.photos.length" class="mt-2 d-flex" style="gap:8px; flex-wrap:wrap;">
            <a v-for="(p, i) in insp.photos" :key="i" v-show="inspPhotoUrls[p]" :href="inspPhotoUrls[p]" target="_blank">
              <img :src="inspPhotoUrls[p]" style="height:92px; width:92px; object-fit:cover; border-radius:6px; border:1px solid #ddd;" />
            </a>
            <span v-if="!insp.photos.some((p) => inspPhotoUrls[p])" class="caption grey--text align-self-center">Ładowanie zdjęć…</span>
          </div>
        </v-card>
      </v-card-text>
    </v-card>

    <v-timeline align-top class="timeline-custom">
      <v-timeline-item
        v-for="(event, index) in carHistory"
        :key="index"
        :color="event.color || 'blue'"
        :icon="event.icon || 'mdi-calendar'"
      >
        <v-card
          elevation="4"
          class="timeline-card mb-4"
          :id="'history-' + event.id"
          :class="{ 'history-highlight': highlightHistoryId === event.id }"
        >
          <!-- Header with date and type -->
          <v-card-title class="d-flex align-center" :style="`background: ${headerBg(event.color)}; color: ${headerFg(event.color)};`">
            <v-icon left :color="headerFg(event.color)" size="24">{{ event.icon }}</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">{{ getHistoryTypeTitle(event.type) }}</div>
              <div class="text-caption" style="opacity: 0.9;">{{ event.date }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-chip
              small
              color="#FFFFFF"
              :text-color="headerBg(event.color)"
              class="font-weight-bold"
            >
              {{ getHistoryTypeTitle(event.type) }}
            </v-chip>
            <v-btn
              v-if="isOwner"
              icon small
              :color="headerFg(event.color)"
              class="ml-1"
              title="Edytuj dane protokołu (head admin)"
              @click="openEditProtocol(event)"
            >
              <v-icon size="20">mdi-pencil</v-icon>
            </v-btn>
          </v-card-title>

          <!-- Main Content -->
          <v-card-text class="pa-4">
            <!-- Basic Description -->
            <div class="mb-3">
              <div class="text-body-1">{{ event.description }}</div>
            </div>

            <!-- Protokół PDF — RENT_START / RENT_END / FORM (legacy). Działa też dla starych. -->
            <div v-if="event.type === 'RENT_START' || event.type === 'RENT_END' || event.type === 'FORM'" class="mb-3">
              <v-btn
                color="red darken-1"
                small
                dark
                @click="downloadRentProtocol(event.id, event.type)"
              >
                <v-icon small left>mdi-file-pdf-box</v-icon>
                Pobierz protokół {{ event.type === 'RENT_END' ? 'zwrotu' : 'najmu' }} (PDF)
              </v-btn>
            </div>

            <!-- Type-specific content -->
            <!-- SERVICE Record -->
            <div v-if="event.type === 'SERVICE'" class="service-details">
              <v-row v-if="event.metadata">
                <v-col cols="12" sm="6" md="4" v-if="event.metadata.serviceType">
                  <div class="text-subtitle-2 grey--text">Typ serwisu</div>
                  <div class="text-body-1 font-weight-medium">{{ event.metadata.serviceType }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="4" v-if="event.metadata.serviceProvider">
                  <div class="text-subtitle-2 grey--text">Warsztat</div>
                  <div class="text-body-1 font-weight-medium">{{ event.metadata.serviceProvider }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="4" v-if="event.metadata.cost">
                  <div class="text-subtitle-2 grey--text">Koszt</div>
                  <div class="text-body-1 font-weight-medium">{{ formatCurrency(event.metadata.cost) }}</div>
                </v-col>
                <v-col cols="12" sm="6" md="4" v-if="event.metadata.mileage">
                  <div class="text-subtitle-2 grey--text">Przebieg</div>
                  <div class="text-body-1 font-weight-medium">{{ formatNumber(event.metadata.mileage) }} km</div>
                </v-col>
                <v-col cols="12" v-if="event.metadata.createdByDriver">
                  <v-chip small color="info" text-color="white" class="mr-2">
                    <v-icon small left>mdi-account-circle</v-icon>
                    Dodane przez kierowcę
                  </v-chip>
                  <v-chip
                    v-if="event.metadata.approvalStatus"
                    small
                    :color="event.metadata.approvalStatus === 'APPROVED' ? 'success' : event.metadata.approvalStatus === 'REJECTED' ? 'error' : 'orange darken-2'"
                    text-color="white"
                  >
                    {{ event.metadata.approvalStatus === 'APPROVED' ? $t('serviceApprovals.approved') : event.metadata.approvalStatus === 'REJECTED' ? $t('serviceApprovals.rejected') : $t('serviceApprovals.statusPending') }}
                  </v-chip>
                </v-col>
                <!-- Akceptacja serwisu wpisanego przez kierowcę -->
                <v-col cols="12" v-if="event.metadata.createdByDriver && event.metadata.approvalStatus === 'PENDING'">
                  <v-btn small color="success" class="mr-2" @click="approveService(event)">
                    <v-icon left small>mdi-check</v-icon>{{ $t('serviceApprovals.approve') }}
                  </v-btn>
                  <v-btn small color="error" @click="rejectService(event)">
                    <v-icon left small>mdi-close</v-icon>{{ $t('serviceApprovals.reject') }}
                  </v-btn>
                </v-col>
                <v-col cols="12" v-else-if="event.metadata.decidedByName" class="pt-0">
                  <div class="text-caption grey--text">
                    {{ event.metadata.decidedByName }} · {{ formatDate(event.metadata.decidedAt) }}
                    <span v-if="event.metadata.rejectReason" class="error--text"> · {{ event.metadata.rejectReason }}</span>
                  </div>
                </v-col>
              </v-row>
              <!-- Poprawa daty/kwoty i usuwanie zdublowanych wpisów (zgłoszenie Ani, 21.10.2026). -->
              <div class="mt-2">
                <v-btn small outlined class="mr-2" @click="openEditService(event)">
                  <v-icon left small>mdi-pencil</v-icon>Edytuj serwis
                </v-btn>
                <v-btn small outlined color="error" @click="deleteService(event)">
                  <v-icon left small>mdi-delete</v-icon>Usuń
                </v-btn>
              </div>
            </div>

            <!-- CRASH Record -->
            <div v-if="event.type === 'CRASH'" class="crash-details">
              <v-row v-if="event.metadata">
                <v-col cols="12" sm="6" v-if="event.metadata.location">
                  <div class="text-subtitle-2 grey--text">Lokalizacja</div>
                  <div class="text-body-1 font-weight-medium">{{ event.metadata.location }}</div>
                </v-col>
                <v-col cols="12" sm="6" v-if="event.metadata.reportedByDriver">
                  <v-chip small color="warning" text-color="white">
                    <v-icon small left>mdi-account-alert</v-icon>
                    Zgłoszone przez kierowcę
                  </v-chip>
                </v-col>
              </v-row>
            </div>

            <!-- MILEAGE Record -->
            <div v-if="event.type === 'MILEAGE'" class="mileage-details">
              <v-row v-if="event.metadata">
                <v-col cols="12" sm="6" v-if="event.metadata.before">
                  <div class="text-subtitle-2 grey--text">Poprzedni przebieg</div>
                  <div class="text-body-1">{{ formatNumber(event.metadata.before.odometer) }} km</div>
                </v-col>
                <v-col cols="12" sm="6" v-if="event.metadata.after">
                  <div class="text-subtitle-2 grey--text">Nowy przebieg</div>
                  <div class="text-body-1 font-weight-bold text-primary">{{ formatNumber(event.metadata.after.odometer) }} km</div>
                </v-col>
                <v-col cols="12" v-if="event.metadata.before && event.metadata.after">
                  <div class="text-subtitle-2 grey--text">Różnica</div>
                  <div class="text-body-1 font-weight-medium">
                    +{{ formatNumber(event.metadata.after.odometer - event.metadata.before.odometer) }} km
                  </div>
                </v-col>
                <v-col cols="12" v-if="event.metadata.updatedByDriver">
                  <v-chip small color="success" text-color="white">
                    <v-icon small left>mdi-speedometer</v-icon>
                    Zaktualizowane przez kierowcę
                  </v-chip>
                </v-col>
              </v-row>
            </div>

            <!-- OTHER Record (messages, etc.) -->
            <div v-if="event.type === 'OTHER'" class="other-details">
              <v-row v-if="event.metadata">
                <v-col cols="12" v-if="event.metadata.messageContent">
                  <div class="text-subtitle-2 grey--text">Treść wiadomości</div>
                  <v-card outlined class="pa-3 mt-2">
                    <div class="text-body-1">{{ event.metadata.messageContent }}</div>
                  </v-card>
                </v-col>
                <v-col cols="12" v-if="event.metadata.isFromDriver">
                  <v-chip small color="info" text-color="white">
                    <v-icon small left>mdi-message-text</v-icon>
                    Wiadomość od kierowcy
                  </v-chip>
                </v-col>
                <v-col cols="12" v-if="event.metadata.isVisibleToDriver !== undefined">
                  <v-chip
                    small
                    :color="event.metadata.isVisibleToDriver ? 'green' : 'orange'"
                    text-color="white"
                  >
                    <v-icon small left>{{ event.metadata.isVisibleToDriver ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                    {{ event.metadata.isVisibleToDriver ? 'Widoczne dla kierowcy' : 'Ukryte przed kierowcą' }}
                  </v-chip>
                </v-col>
              </v-row>
            </div>

            <!-- Driver/Admin Info -->
            <div v-if="event.driverId || event.adminId" class="mt-3">
              <div class="text-subtitle-2 grey--text mb-2">Utworzone przez</div>
              <v-chip
                small
                :color="event.driverId ? 'primary' : 'secondary'"
                text-color="white"
              >
                <v-icon small left>{{ event.driverId ? 'mdi-account-circle' : 'mdi-shield-account' }}</v-icon>
                {{ event.driverId ? 'Kierowca' : 'Administrator' }}
                {{ event.driver ? `- ${event.driver.firstName} ${event.driver.lastName}` : '' }}
                {{ event.admin ? `- ${event.admin.firstName} ${event.admin.lastName}` : '' }}
              </v-chip>
            </div>

            <!-- Files Section -->
            <div v-if="event.file && event.file.length > 0" class="mt-4">
              <div class="text-subtitle-2 grey--text mb-2">Załączniki ({{ event.file.length }})</div>
              <v-row>
                <v-col cols="auto" v-for="(file, fileIndex) in event.file" :key="fileIndex">
                  <v-btn
                    @click="downloadSingleFile(file.id)"
                    color="info"
                    small
                    outlined
                  >
                    <v-icon small left>mdi-download</v-icon>
                    {{ file.originalName || `Plik ${fileIndex + 1}` }}
                  </v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    @click="downloadImage(event.file)"
                    color="primary"
                    small
                  >
                    <v-icon small left>mdi-download-multiple</v-icon>
                    Pobierz wszystkie
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Signatures for FORM records -->
            <v-row v-if="(event.signature || event.workerSignature) && event.type === 'FORM'" class="mt-4">
              <v-col cols="12" md="6" v-if="event.signature">
                <div class="text-subtitle-2 grey--text mb-2">Podpis kierowcy</div>
                <v-img
                  :src="event.signature"
                  max-width="400"
                  max-height="200"
                  class="rounded-lg border"
                  alt="Podpis kierowcy"
                  contain
                ></v-img>
              </v-col>
              <v-col cols="12" md="6" v-if="event.workerSignature">
                <div class="text-subtitle-2 grey--text mb-2">Podpis pracownika</div>
                <v-img
                  :src="event.workerSignature"
                  max-width="400"
                  max-height="200"
                  class="rounded-lg border"
                  alt="Podpis pracownika"
                  contain
                ></v-img>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Protokół najmu / zdania — pola + porównanie -->
          <div v-if="['FORM','RENT_START','RENT_END'].includes(event.type)">
            <v-divider></v-divider>
            <v-card-text>
              <div class="d-flex align-center flex-wrap mb-3">
                <v-checkbox
                  label="Dodaj do porównania"
                  :value="event.id"
                  v-model="selectedTables"
                  @change="handleSelection"
                  class="ma-0"
                  hide-details
                ></v-checkbox>
                <v-btn
                  v-if="event.type === 'RENT_END'"
                  small
                  color="primary"
                  class="ml-3"
                  :loading="comparingEndId === event.id"
                  @click="compareEndWithStart(event)"
                >
                  <v-icon left small>mdi-compare</v-icon>Porównaj z protokołem odbioru
                </v-btn>
                <v-spacer></v-spacer>
                <v-chip small :color="event.type === 'RENT_END' ? 'deep-orange' : 'info'" text-color="white">
                  {{ event.type === 'RENT_END' ? 'Protokół zdania' : 'Protokół najmu' }}
                </v-chip>
              </div>

              <div v-if="event.metadata && event.metadata.length">
                <div class="text-subtitle-2 grey--text mb-2">Pola z protokołu</div>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Element</th>
                        <th class="text-center">Stan</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(meta, i) in event.metadata" :key="i">
                        <td class="text-body-2">{{ meta.name }}</td>
                        <td class="text-center">
                          <v-icon
                            :color="meta.value ? 'green' : 'red'"
                            size="20"
                          >
                            {{ meta.value ? 'mdi-check-circle' : 'mdi-close-circle' }}
                          </v-icon>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-card-text>
          </div>
        </v-card>
      </v-timeline-item>
    </v-timeline>

    <!-- Edycja protokołu (head admin) — poprawa twardych danych, np. przebiegu -->
    <v-dialog v-model="editService.open" max-width="520px">
      <v-card v-if="editService.event">
        <v-card-title>
          <v-icon left color="primary">mdi-car-cog</v-icon>
          Edytuj serwis
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="editService.date" type="date" label="Data wykonania serwisu" outlined dense />
          <v-textarea v-model="editService.description" label="Opis" outlined dense rows="3" />
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="editService.mileage" type="number" label="Przebieg" suffix="km" outlined dense />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="editService.cost" type="number" step="0.01" label="Koszt" suffix="zł" outlined dense />
            </v-col>
          </v-row>
          <div class="text-caption grey--text">Zmiana zapisze się w logu audytu.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editService.open = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="editService.saving" :disabled="!editService.date" @click="zapiszEdycjeSerwisu">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editProto.open" max-width="640px" scrollable>
      <v-card v-if="editProto.event">
        <v-card-title>
          <v-icon left color="primary">mdi-pencil</v-icon>
          Edytuj protokół — {{ getHistoryTypeTitle(editProto.event.type) }}
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" dense outlined class="mb-3">
            Edytujesz twarde dane już utworzonego protokołu. Zmiana zostanie zapisana w logu audytu.
          </v-alert>
          <v-text-field
            v-model="editProto.description"
            label="Opis"
            outlined dense hide-details class="mb-3"
          ></v-text-field>
          <div v-if="editProto.fields.length" class="text-caption grey--text mb-1">Dane protokołu (np. przebieg):</div>
          <v-text-field
            v-for="(f, i) in editProto.fields"
            :key="i"
            v-model="f.value"
            :label="f.label"
            outlined dense hide-details class="mb-2"
          ></v-text-field>
          <div v-if="!editProto.fields.length" class="text-caption grey--text">Ten protokół nie ma edytowalnych pól danych — możesz zmienić tylko opis.</div>

          <v-divider class="my-3"></v-divider>
          <v-checkbox
            v-model="editProto.syncOdo"
            label="Ustaw też jako aktualny przebieg auta"
            hide-details dense class="mt-0"
          ></v-checkbox>
          <v-text-field
            v-if="editProto.syncOdo"
            v-model.number="editProto.carOdometer"
            type="number"
            label="Aktualny przebieg auta"
            suffix="km"
            outlined dense hide-details class="mt-2"
          ></v-text-field>
          <div v-if="editProto.syncOdo" class="text-caption grey--text mt-1">
            Zaktualizuje licznik auta widoczny we Flocie i garażu. Można obniżyć, jeśli wcześniej wpisano za dużo.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editProto.open = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="editProto.saving" @click="saveProtocol">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

        <v-row>
          <v-col cols="12">
            <v-btn
              @click="(skip++, initialize())"
              block
              large
              color="info"
              class="text-h6 font-weight-bold elevation-2"
            >
              <v-icon left>mdi-reload</v-icon>
              Załaduj więcej
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import * as dayjs from "dayjs";
import Swal from "sweetalert2";
import { canAccessSubtab } from "@/services/permissions";
import { translateError } from "@/services/translateError";
import SignatureField from "@/components/SignatureField.vue";
const axios = require("axios");

export default {
  components: { SignatureField },
  data() {
    return {
      myCanUseFacsimile: false,
      myFacsimile: null,
      workerFacsimileStart: null,
      workerFacsimileEnd: null,
      optionsCanvas: {
        penColor: 'black',
        backgroundColor: 'white'
      },
            comparedHeaders: [
        { title: "Nazwa", value: "name" },
        { title: "Wartość", value: "value" },
      ],
      showDialog: false,
      selectedTables: [],
      comparedTables: [],
      comparisonInfo: { first: {}, final: {} },
      comparingEndId: null,
      endRent: { endMileage: '', photos: [], endTime: '' },
      endRentPhotoPreviews: [],
      rentPhotoPreviews: [],
      carHistory: [],
      // Edycja protokołu przez head admina (poprawa twardych danych, np. przebiegu).
      editProto: { open: false, event: null, description: '', fields: [], syncOdo: false, carOdometer: null, saving: false },
      editService: { open: false, event: null, date: '', description: '', mileage: '', cost: '', saving: false },
      // Rodzaj paliwa z auta
      carFuelType: 'BENZYNA',
      // Nowe pola paliwa warunkowe
      fuelLevelPetrol: null,
      fuelLevelDiesel: null,
      fuelLevelLPG: null,
      fuelLevelScaleOptions: ['E', '1/4', '1/2', '3/4', 'F'],
      // Nowe zero-jedynkowe pola protokołu
      fuelLevel: null,
      exteriorCondition: null,
      interiorCondition: null,
      tireCondition: null,
      systemsCondition: null,
      fuelLevels: [
        { value: 'full', label: 'Pełny' },
        { value: '3/4', label: '3/4' },
        { value: '1/2', label: '1/2' },
        { value: '1/4', label: '1/4' },
        { value: 'reserve', label: 'Rezerwa' }
      ],
      skip: 1,
      valid: false,
      currentCarId: null,
      // Inspekcje pojazdu (self-inspekcje kierowców / inspekcje admina, ze zdjęciami).
      carInspections: [],
      inspPhotoUrls: {},
      inspLoading: false,
      inspReviewingId: null,
      inspectionItems: [
        { key: 'exterior', label: 'Nadwozie (rysy, wgniecenia)' },
        { key: 'interior', label: 'Wnętrze' },
        { key: 'lights', label: 'Światła' },
        { key: 'tires', label: 'Opony' },
        { key: 'documents', label: 'Dokumenty (dowód, OC)' },
        { key: 'cleanliness', label: 'Czystość' },
      ],
      highlightHistoryId: null,
      licenses: [],
      currentLicenses: [],
      rules: {
        required: (value) => !!value || "Wymagane.",
      },
      newLicense:{
        licenseNo: '',
        text: '',
        city: ''
      },
      dialog: false,
      tab: 0,
      departments: [],
      drivers: [],
      carDetails: { name: "", plate: "" },
      rent: {
        startTime: "",
        startDate: "",
        endDate: "",
        price: "",
        paymentMethod: "",
        description: "",
        photos: [],
        startMileage: "",
      },
      paymentMethods: ["Przelew", "Gotówka"],
      service: { photos: [], invoice: "", odometer: "" },
      crash: {
        photos: [],
        insuranceInfo: "",
        date: "",
        fixed: false,
        fixedDate: "",
        incidentNumber: "",
      },
  protocol: [
  // Poziom paliwa
  { id: 1,  label: "Poziom paliwa: pełny", checked: false },
  { id: 2,  label: "Poziom paliwa: 3/4", checked: false },
  { id: 3,  label: "Poziom paliwa: 1/2", checked: false },
  { id: 4,  label: "Poziom paliwa: 1/4", checked: false },
  { id: 5,  label: "Poziom paliwa: rezerwa", checked: false },

  // Wyposażenie i dokumenty
  { id: 6,  label: "Dowód rejestracyjny", checked: false },
  { id: 7,  label: "Polisa OC / AC", checked: false },
  { id: 8,  label: "Karta pojazdu", checked: false },
  { id: 9,  label: "Kluczyki", checked: false },
  { id: 10, label: "Gaśnica", checked: false },
  { id: 11, label: "Apteczka", checked: false },
  { id: 12, label: "Trójkąt ostrzegawczy", checked: false },
  { id: 13, label: "Kamizelka odblaskowa", checked: false },
  { id: 14, label: "Koło zapasowe / zestaw naprawczy", checked: false },
  { id: 15, label: "Uchwyt do telefonu", checked: false },
  { id: 16, label: "Taksometr", checked: false },
  { id: 17, label: "Kasa fiskalna / terminal płatniczy", checked: false },
  { id: 18, label: "Tablet / telefon służbowy", checked: false },
  { id: 19, label: "Fotelik / podkładka dziecięca", checked: false },

  // Stan techniczny pojazdu
  { id: 20, label: "Zewnętrzny stan: bez uszkodzeń", checked: false },
  { id: 21, label: "Zewnętrzny stan: zarysowania / wgniecenia", checked: false },
  { id: 22, label: "Wnętrze: czyste", checked: false },
  { id: 23, label: "Wnętrze: zabrudzone / uszkodzenia tapicerki", checked: false },
  { id: 24, label: "Stan opon: dobry", checked: false },
  { id: 25, label: "Stan opon: średni", checked: false },
  { id: 26, label: "Stan opon: zły", checked: false },
  { id: 27, label: "Światła / wycieraczki / klima / taksometr / terminal – sprawne", checked: false },
  { id: 28, label: "Światła / wycieraczki / klima / taksometr / terminal – uwagi", checked: false }
],
      search: "",
      driversEndRent: [],
      page: 1,
    };
  },
  computed: {
    // Head admin = właściciel platformy (z tokenu: actualRole/isHead/hasAccessToAllCompanies).
    isOwner() {
      try {
        const t = localStorage.getItem('token');
        if (!t) return false;
        const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return p.actualRole === 'headadmin' || p.isHead === true || p.hasAccessToAllCompanies === true;
      } catch (e) {
        return false;
      }
    },
    groupedComparisonData() {
      if (!this.comparedTables[0] || !this.comparedTables[1]) {
        return { groups1: {}, groups2: {} };
      }

      const groups1 = this.groupProtocolItems(this.comparedTables[0]);
      const groups2 = this.groupProtocolItems(this.comparedTables[1]);

      return { groups1, groups2 };
    }
  },

  async created() {
    const params = new URLSearchParams(document.location.search);
    const id = params.get("id");
    this.currentCarId = id;
    // Deep-link z powiadomienia „nowy serwis do akceptacji" — po załadowaniu
    // historii przewijamy do wpisu i podświetlamy go.
    this.highlightHistoryId = params.get("historyId") || null;
    await this.initialize(id);
    this.fetchCarInspections(id);
    if (this.highlightHistoryId) {
      this.$nextTick(() => {
        const el = document.getElementById('history-' + this.highlightHistoryId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    // Simulating backend call
    await this.fetchDepartments();
    await this.getDriversSelect();
    await this.fetchMyFacsimile();
    // const licenses = await this.fetchLicenses();
  },
  watch: {
    tab(newVal) {
      if (newVal === 1 && !this.rent.startTime) this.rent.startTime = this.godzinaTeraz();
      if (newVal === 2 && !this.endRent.endTime) this.$set(this.endRent, 'endTime', this.godzinaTeraz());
      // Resize signature pads when switching to tab 1 (Protokół Najmu)
      if (newVal === 1) {
        // Use setTimeout to ensure DOM is fully rendered after v-show transition
        setTimeout(() => {
          try {
            if (this.$refs.signaturePad?.$el) {
              this.$refs.signaturePad.resizeCanvas();
            }
            if (this.$refs.workerSignaturePad?.$el) {
              this.$refs.workerSignaturePad.resizeCanvas();
            }
          } catch (e) {
            console.log('Signature pad not ready yet');
          }
        }, 100);
      }
      // Resize signature pads when switching to tab 2 (Protokół Zakończenia Najmu)
      if (newVal === 2) {
        setTimeout(() => {
          try {
            if (this.$refs.endRentSignaturePad?.$el) {
              this.$refs.endRentSignaturePad.resizeCanvas();
            }
            if (this.$refs.endRentWorkerSignaturePad?.$el) {
              this.$refs.endRentWorkerSignaturePad.resizeCanvas();
            }
          } catch (e) {
            console.log('Signature pad not ready yet');
          }
        }, 100);
      }
    },
  },
  methods: {
    canAccess(sub) { return canAccessSubtab('fleet-details', sub); },
    // ===== Inspekcje pojazdu (self-inspekcje kierowców / admina, ze zdjęciami) =====
    formatInspDateTime(d) {
      return d ? new Date(d).toLocaleString('pl-PL') : '—';
    },
    inspStatusLabel(s) {
      return { PENDING_REVIEW: 'Do przeglądu', ACCEPTED: 'Zaakceptowana', FLAGGED: 'Usterka' }[s] || s || '—';
    },
    inspStatusColor(s) {
      return { PENDING_REVIEW: 'warning', ACCEPTED: 'success', FLAGGED: 'error' }[s] || 'grey';
    },
    inspMileage(insp) {
      return insp && insp.checklist && insp.checklist.odometer ? insp.checklist.odometer : null;
    },
    // Ocena pozycji checklisty → etykieta + kolor (obsługuje też stare inspekcje boolean).
    ratingMeta(v) {
      if (v === 'VERY_GOOD') return { label: 'Bardzo dobry', color: 'green darken-1' };
      if (v === 'GOOD') return { label: 'Dobry', color: 'amber darken-2' };
      if (v === 'BAD') return { label: 'Zły', color: 'red darken-1' };
      if (v === true) return { label: 'OK', color: 'green darken-1' };
      if (v === false) return { label: 'Nie', color: 'grey' };
      return null;
    },
    inspRatings(insp) {
      const cl = (insp && insp.checklist) || {};
      const out = [];
      this.inspectionItems.forEach((it) => {
        if (cl[it.key] === undefined || cl[it.key] === null) return;
        const meta = this.ratingMeta(cl[it.key]);
        if (meta) out.push({ name: it.label, label: meta.label, color: meta.color });
      });
      return out;
    },
    async fetchCarInspections(carId) {
      if (!carId) return;
      this.inspLoading = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'car-inspections/car/' + carId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = (res.data && res.data.data) || {};
        this.carInspections = d.inspections || [];
        this.loadCarInspectionPhotos();
      } catch (e) {
        console.log(e);
      } finally {
        this.inspLoading = false;
      }
    },
    // Zdjęcia serwowane przez authed POST download-file/:id → pobieramy jako blob
    // i tworzymy lokalne URL-e (Vue.set, by reaktywnie odświeżyć miniatury).
    async loadCarInspectionPhotos() {
      const ids = [];
      this.carInspections.forEach((insp) => (insp.photos || []).forEach((id) => { if (id && !this.inspPhotoUrls[id]) ids.push(id); }));
      for (const id of ids) {
        try {
          const res = await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'aws/download-file/' + id,
            responseType: 'blob',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          });
          this.$set(this.inspPhotoUrls, id, window.URL.createObjectURL(res.data));
        } catch (e) { /* pojedyncze błędy zdjęć pomijamy */ }
      }
    },
    async reviewCarInspection(id, decision) {
      this.inspReviewingId = id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'car-inspections/' + id + '/review',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { decision },
        });
        await this.fetchCarInspections(this.currentCarId);
      } catch (e) {
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.inspReviewingId = null;
      }
    },
    // ===== Edycja protokołu (head admin): twarde dane, np. przebieg =====
    metaLabel(path) {
      const map = {
        mileage: 'Przebieg (km)',
        cost: 'Koszt',
        'before.odometer': 'Przebieg przed (km)',
        'after.odometer': 'Przebieg po (km)',
        odometer: 'Przebieg (km)',
        amount: 'Kwota',
      };
      return map[path] || path;
    },
    // Zbiera tylko liczbowe pola metadanych (przebieg/koszt/odometer) jako edytowalne.
    flattenNumbers(obj, prefix, out) {
      if (obj === null || obj === undefined) return out;
      if (Array.isArray(obj)) {
        obj.forEach((v, i) => this.flattenNumbers(v, prefix ? `${prefix}.${i}` : String(i), out));
        return out;
      }
      if (typeof obj === 'object') {
        Object.keys(obj).forEach((k) => this.flattenNumbers(obj[k], prefix ? `${prefix}.${k}` : k, out));
        return out;
      }
      if (typeof obj === 'number') {
        out.push({ path: prefix, label: this.metaLabel(prefix), value: obj });
      }
      return out;
    },
    setPath(obj, path, value) {
      const parts = path.split('.');
      let o = obj;
      for (let i = 0; i < parts.length - 1; i++) o = o[parts[i]];
      o[parts[parts.length - 1]] = value;
    },
    openEditProtocol(event) {
      const fields = this.flattenNumbers(event.metadata || {}, '', []);
      // Sensowny prefill licznika: after.odometer → mileage → odometer → największa liczba.
      const byPath = (p) => {
        const f = fields.find((x) => x.path === p);
        return f ? f.value : null;
      };
      let odo = byPath('after.odometer');
      if (odo == null) odo = byPath('mileage');
      if (odo == null) odo = byPath('odometer');
      if (odo == null && fields.length) odo = Math.max(...fields.map((f) => f.value));
      this.editProto = {
        open: true,
        event,
        description: event.description || '',
        fields,
        syncOdo: false,
        carOdometer: odo != null ? odo : null,
        saving: false,
      };
    },
    openEditService(event) {
      const m = event.metadata || {};
      this.editService = {
        open: true,
        event,
        date: m.serviceDate || dayjs(event.date).format('YYYY-MM-DD'),
        description: event.description || '',
        mileage: m.mileage || '',
        cost: m.cost != null ? (m.cost / 100).toFixed(2) : '',
        saving: false,
      };
    },
    async zapiszEdycjeSerwisu() {
      this.editService.saving = true;
      try {
        const ev = this.editService.event;
        const res = await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'cars-history/service/' + ev.id,
          data: {
            date: this.editService.date,
            description: this.editService.description,
            mileage: this.editService.mileage,
            cost: this.editService.cost,
          },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const zapisany = res.data && res.data.data;
        if (zapisany) {
          ev.description = zapisany.description;
          ev.metadata = zapisany.metadata;
          ev.date = dayjs(zapisany.createdAt).format('YYYY-MM-DD HH:mm');
        }
        this.editService.open = false;
        Swal.fire({ title: 'Zapisano', icon: 'success', timer: 1300, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', (e.response && e.response.data && e.response.data.message) || 'Nie udało się zapisać serwisu', 'error');
      } finally {
        this.editService.saving = false;
      }
    },
    async deleteService(event) {
      const odp = await Swal.fire({
        icon: 'warning',
        title: 'Usunąć wpis serwisu?',
        text: `${event.date} — ${event.description}`,
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
      });
      if (!odp.isConfirmed) return;
      try {
        await axios({
          method: 'delete',
          url: process.env.VUE_APP_ROOT_API + 'cars-history/' + this.currentCarId + '/' + event.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.carHistory = this.carHistory.filter((e) => e.id !== event.id);
        Swal.fire({ title: 'Usunięto', icon: 'success', timer: 1300, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', (e.response && e.response.data && e.response.data.message) || 'Nie udało się usunąć wpisu', 'error');
      }
    },
    async saveProtocol() {
      this.editProto.saving = true;
      try {
        const ev = this.editProto.event;
        const metadata = JSON.parse(JSON.stringify(ev.metadata || {}));
        this.editProto.fields.forEach((f) => {
          const num = Number(f.value);
          this.setPath(metadata, f.path, Number.isNaN(num) ? f.value : num);
        });
        const payload = { metadata, description: this.editProto.description };
        if (this.editProto.syncOdo && this.editProto.carOdometer !== null && this.editProto.carOdometer !== '') {
          payload.carOdometer = Number(this.editProto.carOdometer);
        }
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'cars-history/record/' + ev.id,
          data: payload,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        // Aktualizacja w miejscu (bez przeładowania całej historii).
        ev.metadata = metadata;
        ev.description = this.editProto.description;
        this.editProto.open = false;
        Swal.fire({ title: 'Zapisano', icon: 'success', timer: 1300, showConfirmButton: false });
      } catch (e) {
        console.error('Save protocol failed:', e);
        alert((e.response && e.response.data && e.response.data.message) || 'Nie udało się zapisać zmian');
      } finally {
        this.editProto.saving = false;
      }
    },
    // ─── Akceptacja serwisów wpisanych przez kierowcę ───
    async approveService(event) {
      const r = await Swal.fire({
        title: this.$t('serviceApprovals.approveTitle'),
        html: `${(event.metadata && event.metadata.serviceType) || ''}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4CAF50',
        confirmButtonText: this.$t('serviceApprovals.approve'),
        cancelButtonText: this.$t('common.cancel'),
      });
      if (!r.isConfirmed) return;
      await this.decideService(event, 'approve');
    },
    async rejectService(event) {
      const r = await Swal.fire({
        title: this.$t('serviceApprovals.rejectTitle'),
        html: `${(event.metadata && event.metadata.serviceType) || ''}`,
        input: 'text',
        inputPlaceholder: this.$t('serviceApprovals.rejectReasonPlaceholder'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: this.$t('serviceApprovals.reject'),
        cancelButtonText: this.$t('common.cancel'),
      });
      if (!r.isConfirmed) return;
      await this.decideService(event, 'reject', r.value || undefined);
    },
    async decideService(event, action, reason) {
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'service-approvals/' + event.id + '/' + action,
          data: action === 'reject' ? { reason } : {},
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        // Aktualizacja w miejscu — backend zwraca zaktualizowany rekord.
        const updated = res.data && res.data.data;
        if (updated && updated.metadata) {
          event.metadata = updated.metadata;
        } else {
          event.metadata = { ...event.metadata, approvalStatus: action === 'approve' ? 'APPROVED' : 'REJECTED' };
        }
      } catch (e) {
        Swal.fire(this.$t('common.error'), (e.response && e.response.data && e.response.data.message) || this.$t('serviceApprovals.actionFailed'), 'error');
      }
    },
    async fetchMyFacsimile() {
      try {
        const me = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.myCanUseFacsimile = !!me.data.data.canUseFacsimile;
        this.myFacsimile = me.data.data.facsimileSignature || null;
      } catch (e) { /* faksymilka opcjonalna */ }
    },
    useWorkerFacsimile(which) {
      if (!this.myFacsimile) return;
      if (which === 'start') {
        this.workerFacsimileStart = this.myFacsimile;
        try { this.$refs.workerSignaturePad?.clearSignature(); } catch (e) { /* ignore */ }
      } else {
        this.workerFacsimileEnd = this.myFacsimile;
        try { this.$refs.endRentWorkerSignaturePad?.clearSignature(); } catch (e) { /* ignore */ }
      }
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
      goBack() {
      location.href = "/fleet";
    },
    onRentPhotos(e) {
      if (!Array.isArray(this.rent.photos)) this.$set(this.rent, 'photos', []);
      for (const f of (e.target.files || [])) {
        this.rent.photos.push(f);
        this.rentPhotoPreviews.push(
          f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : '',
        );
      }
      // Czyścimy input, żeby dało się wybrać ten sam plik ponownie.
      e.target.value = '';
    },
    removeRentPhoto(i) {
      this.rent.photos.splice(i, 1);
      const [u] = this.rentPhotoPreviews.splice(i, 1);
      if (u) URL.revokeObjectURL(u);
    },
    onEndRentPhotos(e) {
      if (!Array.isArray(this.endRent.photos)) this.$set(this.endRent, 'photos', []);
      for (const f of (e.target.files || [])) {
        this.endRent.photos.push(f);
        this.endRentPhotoPreviews.push(
          f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : '',
        );
      }
      e.target.value = '';
    },
    removeEndRentPhoto(i) {
      this.endRent.photos.splice(i, 1);
      const [u] = this.endRentPhotoPreviews.splice(i, 1);
      if (u) URL.revokeObjectURL(u);
    },
    // Protokoły sprzed 09.10.2026 mają samą datę (północ UTC) — bez godziny,
    // żeby nie pokazywać zmyślonej „02:00".
    dataProtokolu(chwila) {
      const d = new Date(chwila);
      const bezGodziny = d.getUTCHours() === 0 && d.getUTCMinutes() === 0
        && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
      return bezGodziny ? d.toISOString().slice(0, 10) : dayjs(d).format("YYYY-MM-DD HH:mm");
    },
    godzinaTeraz() {
      return dayjs().format("HH:mm");
    },
    // Data + godzina protokołu jako pełna chwila (strefa przeglądarki = Polska).
    // Bez daty = dzisiaj.
    chwilaProtokolu(data, godzina) {
      const dzien = data || dayjs().format("YYYY-MM-DD");
      return new Date(`${dzien}T${godzina}:00`).toISOString();
    },
    async saveEndRent() {
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        this.endRent.photos?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Missing Image");
          }
        });
        if (!this.endRent.endTime) {
          await this.toggleLoading(false);
          Swal.fire("Brak godziny", "Podaj godzinę zwrotu auta — decyduje o czynszu za dzień zwrotu i o odpowiedzialności za mandaty.", "warning");
          return;
        }
        formData.append("endDate", this.chwilaProtokolu(this.endRent.endDate, this.endRent.endTime));
        formData.append("description", this.endRent.description);
        formData.append("carId", this.currentCarId);
        formData.append("driverId", this.endRent.driverId);
        formData.append("endMileage", this.endRent.endMileage || 0);
        const endFuelMetadata = [];
        if (this.carFuelType === 'DIESEL') {
          endFuelMetadata.push({ name: 'Poziom diesla', value: this.fuelLevelDiesel || '' });
        } else if (this.carFuelType === 'BENZYNA') {
          endFuelMetadata.push({ name: 'Poziom benzyny', value: this.fuelLevelPetrol || '' });
        } else if (this.carFuelType === 'BENZYNA_LPG') {
          endFuelMetadata.push({ name: 'Poziom benzyny', value: this.fuelLevelPetrol || '' });
          endFuelMetadata.push({ name: 'Poziom LPG', value: this.fuelLevelLPG || '' });
        }
        formData.append(
          "metadata",
          JSON.stringify([
            ...endFuelMetadata,
            ...this.protocol.map((it) => {
              return {
                value: it.checked,
                name: it.label,
              };
            })
          ])
        );
        const { isEmpty, data } = this.$refs.endRentSignaturePad.saveSignature();
        const { isEmpty: workerIsEmpty, data: workerData } = this.$refs.endRentWorkerSignaturePad.saveSignature();
        formData.append("signature", !isEmpty ? data : null);
        formData.append("workerSignature", this.workerFacsimileEnd || (!workerIsEmpty ? workerData : null));

        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history/end-rent",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.skip = 1;
        await this.toggleLoading(false);
        await this.initialize();
                this.endRent.driverId = null;
        this.endRent.description = null;
        this.endRent.price = null;
        this.endRent.endDate = null;
        this.endRent.endTime = this.godzinaTeraz();
        this.endRent.endMileage = null;
        this.fuelLevelPetrol = null;
        this.fuelLevelDiesel = null;
        this.fuelLevelLPG = null;
        this.protocol = this.protocol.map((it) => {
          return {
            checked: false,
            label: it.label,
          };
        });
        this.$refs.endRentSignaturePad.undoSignature();
        this.$refs.endRentWorkerSignaturePad.undoSignature();
        this.endRent.photos = [];
        this.endRentPhotoPreviews.forEach((u) => u && URL.revokeObjectURL(u));
        this.endRentPhotoPreviews = [];
        this.dialog = false;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
     async saveCrash() {
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        this.crash.photos?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Missing Image");
          }
        });
        formData.append("incidentDate", this.crash.date);
        formData.append("description", this.crash.description);
        formData.append("insuranceInfo", this.crash.insuranceInfo);
        formData.append("caseNumber", this.crash.incidentNumber);
        formData.append("carId", this.currentCarId);
        formData.append("driverId", this.crash.driverId);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history/crash",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.skip = 1;
        await this.toggleLoading(false);
        await this.initialize();
        this.crash.date = null;
        this.crash.description = null;
        this.crash.insuranceInfo = null;
        this.crash.incidentNumber = null;
        this.crash.driverId = null;
        this.crash.photos = []
        this.dialog = false;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async saveService() {
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        this.service.photos?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Missing Image");
          }
        });
        formData.append("odometer", parseInt(this.service.odometer));
        formData.append("serviceDate", this.service.date);
        formData.append("description", this.service.description);
        formData.append("carId", this.currentCarId);
        formData.append("driverId", this.service.driverId);
        
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history/service",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.skip = 1;
        await this.toggleLoading(false);
        await this.initialize();
                this.service.odometer = null;
        this.service.date = null;
        this.service.description = null;
        this.service.driverId = null;
        this.service.photos = []
        this.dialog = false;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async handleSelection() {
      if (this.selectedTables.length > 2) {
        this.selectedTables.shift(); // Keep only the last two selected
      }
      if (this.selectedTables.length === 2) {
        // Get the selected protocols
        const protocol1 = this.carHistory.find((it) => it.id === this.selectedTables[0]);
        const protocol2 = this.carHistory.find((it) => it.id === this.selectedTables[1]);

        // Check if protocols exist and have metadata
        if (!protocol1 || !protocol2 || !protocol1.metadata || !protocol2.metadata) {
          Swal.fire("Błąd", "Nie można porównać wybranych protokołów - brak danych", "error");
          this.selectedTables = [];
          return;
        }

        // Można porównywać protokoły najmu/zdania (FORM = stare, RENT_START/RENT_END = nowe)
        const comparableTypes = ['FORM', 'RENT_START', 'RENT_END'];
        if (!comparableTypes.includes(protocol1.type) || !comparableTypes.includes(protocol2.type)) {
          Swal.fire("Błąd", "Można porównywać tylko protokoły najmu/zdania", "warning");
          this.selectedTables = [];
          return;
        }

        // Sort protocols by date to determine first and final
        const sortedProtocols = [protocol1, protocol2].sort((a, b) => new Date(a.date) - new Date(b.date));
        const firstProtocol = sortedProtocols[0];
        const finalProtocol = sortedProtocols[1];

        this.showDialog = true;
        this.comparedTables = [];
        this.comparedTables.push(firstProtocol.metadata);
        this.comparedTables.push(finalProtocol.metadata);

        // Store protocol info for display
        this.comparisonInfo = {
          first: {
            date: firstProtocol.date,
            type: firstProtocol.type,
            id: firstProtocol.id
          },
          final: {
            date: finalProtocol.date,
            type: finalProtocol.type,
            id: finalProtocol.id
          }
        };

        console.log('Comparison data:', {
          first: firstProtocol,
          final: finalProtocol,
          comparedTables: this.comparedTables
        });
      }
    },
    // Dedykowany przycisk przy protokole ZDANIA: znajdź na backendzie pasujący
    // protokół ODBIORU (ten sam kierowca+auto) i otwórz porównanie.
    async compareEndWithStart(endEvent) {
      this.comparingEndId = endEvent.id;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + "cars-history/rent-start-for-end/" + endEvent.id,
          { headers: { Authorization: "Bearer " + localStorage.getItem("token") } },
        );
        const start = res.data && res.data.data ? res.data.data.start : null;
        if (!start) {
          Swal.fire("Brak protokołu odbioru", "Nie znaleziono protokołu odbioru (najmu) dla tego kierowcy i auta.", "info");
          return;
        }
        const parseMeta = (m) => (typeof m === "string" ? JSON.parse(m) : m) || [];
        this.comparedTables = [parseMeta(start.metadata), endEvent.metadata || []];
        this.comparisonInfo = {
          first: {
            date: start.startDate
              ? dayjs(start.startDate).format("YYYY-MM-DD HH:mm")
              : dayjs(start.createdAt).format("YYYY-MM-DD HH:mm"),
            type: "RENT_START",
            id: start.id,
          },
          final: { date: endEvent.date, type: "RENT_END", id: endEvent.id },
        };
        this.showDialog = true;
      } catch (e) {
        Swal.fire("Błąd", (e.response && e.response.data && e.response.data.message ? e.response.data.message.toString() : "Nie udało się pobrać protokołu odbioru"), "error");
      } finally {
        this.comparingEndId = null;
      }
    },
    getRowClass(item, table) {
      const otherTable =
        table === "table1" ? this.comparedTables[1] : this.comparedTables[0];
      const matchingItem = otherTable.find((i) => i.name === item.name);
      if (matchingItem && matchingItem.value !== item.value) {
        return "comparison-different";
      }
      if (matchingItem && matchingItem.value === item.value) {
        return "comparison-same";
      }
      return "comparison-missing";
    },
    getItemStatusColor(item, table) {
      const otherTable =
        table === "table1" ? this.comparedTables[1] : this.comparedTables[0];
      const matchingItem = otherTable.find((i) => i.name === item.name);
      if (!matchingItem) {
        return "warning";
      }
      if (matchingItem.value !== item.value) {
        return "error";
      }
      return "success";
    },
    getItemStatusIcon(item, table) {
      const otherTable =
        table === "table1" ? this.comparedTables[1] : this.comparedTables[0];
      const matchingItem = otherTable.find((i) => i.name === item.name);
      if (!matchingItem) {
        return "mdi-help";
      }
      if (matchingItem.value !== item.value) {
        return "mdi-close";
      }
      return "mdi-check";
    },
    getMatchingItemsCount() {
      if (!this.comparedTables[0] || !this.comparedTables[1]) return 0;
      let count = 0;
      this.comparedTables[0].forEach(item1 => {
        const item2 = this.comparedTables[1].find(i => i.name === item1.name);
        if (item2 && item1.value === item2.value) {
          count++;
        }
      });
      return count;
    },
    getDifferentItemsCount() {
      if (!this.comparedTables[0] || !this.comparedTables[1]) return 0;
      let count = 0;
      this.comparedTables[0].forEach(item1 => {
        const item2 = this.comparedTables[1].find(i => i.name === item1.name);
        if (item2 && item1.value !== item2.value) {
          count++;
        }
      });
      return count;
    },
    groupProtocolItems(items) {
      const groups = {
        fuel: {
          title: 'Poziom paliwa',
          icon: 'mdi-gas-station',
          color: 'amber',
          items: []
        },
        equipment: {
          title: 'Wyposażenie i dokumenty',
          icon: 'mdi-briefcase',
          color: 'blue',
          items: []
        },
        technical: {
          title: 'Stan techniczny pojazdu',
          icon: 'mdi-car-wrench',
          color: 'green',
          items: []
        }
      };

      items.forEach(item => {
        if (item.name.toLowerCase().includes('poziom paliwa') || item.name.toLowerCase().includes('paliwa')) {
          groups.fuel.items.push(item);
        } else if (item.name.toLowerCase().includes('opon') || item.name.toLowerCase().includes('stan opon')) {
          // Create a separate tires group or add to technical
          if (!groups.tires) {
            groups.tires = {
              title: 'Stan opon',
              icon: 'mdi-car-tire-alert',
              color: 'purple',
              items: []
            };
          }
          groups.tires.items.push(item);
        } else if (item.name.toLowerCase().includes('zewnętrzny') ||
                   item.name.toLowerCase().includes('uszkodzeń') ||
                   item.name.toLowerCase().includes('zarysowania') ||
                   item.name.toLowerCase().includes('wgniecenia')) {
          // Create a separate damages group or add to technical
          if (!groups.damages) {
            groups.damages = {
              title: 'Uszkodzenia zewnętrzne',
              icon: 'mdi-car-brake-alert',
              color: 'red',
              items: []
            };
          }
          groups.damages.items.push(item);
        } else if (item.name.toLowerCase().includes('wnętrze') ||
                   item.name.toLowerCase().includes('tapicerki') ||
                   item.name.toLowerCase().includes('zabrudzone')) {
          // Create interior group
          if (!groups.interior) {
            groups.interior = {
              title: 'Stan wnętrza',
              icon: 'mdi-car-seat',
              color: 'indigo',
              items: []
            };
          }
          groups.interior.items.push(item);
        } else if (item.name.toLowerCase().includes('światła') ||
                   item.name.toLowerCase().includes('wycieraczki') ||
                   item.name.toLowerCase().includes('klima') ||
                   item.name.toLowerCase().includes('taksometr') ||
                   item.name.toLowerCase().includes('terminal')) {
          // Create systems group
          if (!groups.systems) {
            groups.systems = {
              title: 'Systemy pojazdu',
              icon: 'mdi-car-electric',
              color: 'teal',
              items: []
            };
          }
          groups.systems.items.push(item);
        } else if (item.name.toLowerCase().includes('dowód') ||
                   item.name.toLowerCase().includes('polisa') ||
                   item.name.toLowerCase().includes('karta') ||
                   item.name.toLowerCase().includes('kluczyki')) {
          // Documents and keys
          if (!groups.documents) {
            groups.documents = {
              title: 'Dokumenty i klucze',
              icon: 'mdi-file-document',
              color: 'brown',
              items: []
            };
          }
          groups.documents.items.push(item);
        } else if (item.name.toLowerCase().includes('gaśnica') ||
                   item.name.toLowerCase().includes('apteczka') ||
                   item.name.toLowerCase().includes('trójkąt') ||
                   item.name.toLowerCase().includes('kamizelka') ||
                   item.name.toLowerCase().includes('koło zapasowe') ||
                   item.name.toLowerCase().includes('zestaw naprawczy')) {
          // Safety equipment
          if (!groups.safety) {
            groups.safety = {
              title: 'Wyposażenie bezpieczeństwa',
              icon: 'mdi-shield-car',
              color: 'orange',
              items: []
            };
          }
          groups.safety.items.push(item);
        } else if (item.name.toLowerCase().includes('uchwyt') ||
                   item.name.toLowerCase().includes('taksometr') ||
                   item.name.toLowerCase().includes('kasa') ||
                   item.name.toLowerCase().includes('terminal') ||
                   item.name.toLowerCase().includes('tablet') ||
                   item.name.toLowerCase().includes('telefon') ||
                   item.name.toLowerCase().includes('fotelik')) {
          // Work equipment
          if (!groups.work) {
            groups.work = {
              title: 'Wyposażenie robocze',
              icon: 'mdi-briefcase-variant',
              color: 'cyan',
              items: []
            };
          }
          groups.work.items.push(item);
        } else {
          groups.equipment.items.push(item);
        }
      });

      // Remove empty groups
      Object.keys(groups).forEach(key => {
        if (groups[key].items.length === 0) {
          delete groups[key];
        }
      });

      return groups;
    },
    getGroupComparison(group1, group2) {
      let same = 0;
      let different = 0;
      let total = group1.items.length;

      group1.items.forEach(item1 => {
        const item2 = group2.items.find(i => i.name === item1.name);
        if (item2) {
          if (item1.value === item2.value) {
            same++;
          } else {
            different++;
          }
        }
      });

      return { same, different, total };
    },
    getGroupComparisonColor(group1, group2) {
      const comparison = this.getGroupComparison(group1, group2);
      if (comparison.different > 0) {
        return 'error';
      } else if (comparison.same === comparison.total) {
        return 'success';
      } else {
        return 'warning';
      }
    },
    getGroupComparisonText(group1, group2) {
      const comparison = this.getGroupComparison(group1, group2);
      if (comparison.different > 0) {
        return `${comparison.different} różnych`;
      } else if (comparison.same === comparison.total) {
        return 'Wszystkie zgodne';
      } else {
        return `${comparison.same}/${comparison.total} zgodnych`;
      }
    },
    getChangedGroups() {
      if (!this.comparedTables[0] || !this.comparedTables[1]) return [];

      const groups1 = this.groupProtocolItems(this.comparedTables[0]);
      const groups2 = this.groupProtocolItems(this.comparedTables[1]);

      const changedGroups = [];

      Object.keys(groups1).forEach(groupKey => {
        if (groups2[groupKey]) {
          const hasChanges = groups1[groupKey].items.some(item1 => {
            const item2 = groups2[groupKey].items.find(i => i.name === item1.name);
            return item2 && item1.value !== item2.value;
          });

          if (hasChanges) {
            changedGroups.push(groupKey);
          }
        }
      });

      return changedGroups;
    },
    getChangedItemsInGroup(groupKey) {
      if (!this.comparedTables[0] || !this.comparedTables[1]) return [];

      const groups1 = this.groupProtocolItems(this.comparedTables[0]);
      const groups2 = this.groupProtocolItems(this.comparedTables[1]);

      if (!groups1[groupKey] || !groups2[groupKey]) return [];

      const changedItems = [];

      groups1[groupKey].items.forEach(item1 => {
        const item2 = groups2[groupKey].items.find(i => i.name === item1.name);
        if (item2 && item1.value !== item2.value) {
          changedItems.push({
            name: item1.name,
            firstValue: item1.value,
            finalValue: item2.value
          });
        }
      });

      return changedItems;
    },
    getGroupIcon(groupKey) {
      const groups1 = this.groupProtocolItems(this.comparedTables[0] || []);
      return groups1[groupKey] || { title: 'Unknown', icon: 'mdi-help', color: 'grey' };
    },
    cleanItemName(name) {
      return name.replace(/^Poziom paliwa: /, '');
    },
    getItemCardClass(item, position) {
      if (position === 'first') {
        return item.firstValue ? 'border-success' : 'border-error';
      } else {
        return item.finalValue ? 'border-success' : 'border-error';
      }
    },
    getItemChangeColor(item, position) {
      if (position === 'first') {
        return item.firstValue ? 'success' : 'error';
      } else {
        return item.finalValue ? 'success' : 'error';
      }
    },
    getTotalChangesCount() {
      let total = 0;
      this.getChangedGroups().forEach(groupKey => {
        total += this.getChangedItemsInGroup(groupKey).length;
      });
      return total;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getGroupStatusText(groupKey) {
      const changes = this.getChangedItemsInGroup(groupKey);
      const improvements = changes.filter(item => !item.firstValue && item.finalValue).length;
      const deteriorations = changes.filter(item => item.firstValue && !item.finalValue).length;

      if (improvements > deteriorations) {
        return 'Poprawa';
      } else if (deteriorations > improvements) {
        return 'Pogorszenie';
      } else {
        return 'Zmiany';
      }
    },
    getSimpleItemName(name) {
      // Clean up item names to be more driver-friendly
      return name
        .replace(/^Poziom paliwa: /, '')
        .replace(/Stan opon: /, '')
        .replace(/Zewnętrzny stan: /, '')
        .replace(/Wnętrze: /, '')
        .replace(/Światła \/.*– /, '');
    },
    getStatusText(value) {
      return value ? 'OK' : 'Problem';
    },
    getChangeArrowColor(item) {
      if (!item.firstValue && item.finalValue) {
        return 'success'; // Improvement
      } else if (item.firstValue && !item.finalValue) {
        return 'error'; // Deterioration
      }
      return 'warning'; // Other change
    },
    getChangeArrowIcon(item) {
      if (!item.firstValue && item.finalValue) {
        return 'mdi-trending-up'; // Improvement
      } else if (item.firstValue && !item.finalValue) {
        return 'mdi-trending-down'; // Deterioration
      }
      return 'mdi-swap-horizontal'; // Other change
    },
    getSimpleChanges() {
      if (!this.comparedTables[0] || !this.comparedTables[1]) return [];

      const changes = [];
      const table1 = this.comparedTables[0];
      const table2 = this.comparedTables[1];

      // Sprawdzamy paliwo - pokazujemy TYLKO jeśli się zmieniło
      const fuelBefore = this.getFuelLevel(table1);
      const fuelAfter = this.getFuelLevel(table2);

      if (fuelBefore !== fuelAfter) {
        const isGood = this.isFuelLevelBetter(fuelBefore, fuelAfter);
        changes.push({
          what: '⛽ POZIOM PALIWA',
          before: fuelBefore,
          after: fuelAfter,
          beforeGood: fuelBefore !== 'Pusty',
          afterGood: fuelAfter !== 'Pusty',
          isGood: isGood,
          explanation: isGood
            ? 'Kierowca dolewał paliwo - świetnie!'
            : 'Paliwo się skończyło - normalnie przy jeźdzeniu'
        });
      }

      // Paliwo — NOWY format protokołów najmu: pozycje "Poziom diesla/benzyny/LPG"
      // z wartością ze skali E,1/4,1/2,3/4,F. Pokazujemy ZAWSZE (to kluczowy
      // element protokołu odbioru/zdania), też gdy bez zmian.
      const fuelScale = { 'E': 0, '1/4': 1, '1/2': 2, '3/4': 3, 'F': 4 };
      const fuelNames = [];
      [table1, table2].forEach((t) => (t || []).forEach((it) => {
        if (typeof it.name === 'string' && /^Poziom (diesla|benzyny|LPG)/.test(it.name) && !fuelNames.includes(it.name)) {
          fuelNames.push(it.name);
        }
      }));
      fuelNames.forEach((fname) => {
        const bRaw = (table1.find((i) => i.name === fname) || {}).value;
        const aRaw = (table2.find((i) => i.name === fname) || {}).value;
        const before = (bRaw === undefined || bRaw === null || bRaw === '') ? '—' : String(bRaw);
        const after = (aRaw === undefined || aRaw === null || aRaw === '') ? '—' : String(aRaw);
        const bv = fuelScale[before] !== undefined ? fuelScale[before] : -1;
        const av = fuelScale[after] !== undefined ? fuelScale[after] : -1;
        const isGood = av >= bv;
        changes.push({
          what: '⛽ ' + fname.toUpperCase(),
          before,
          after,
          beforeGood: before !== 'E' && before !== '—',
          afterGood: after !== 'E' && after !== '—',
          isGood,
          explanation: before === after
            ? 'Poziom paliwa bez zmian'
            : (isGood ? 'Kierowca dolał paliwa' : 'Poziom paliwa niższy niż przy odbiorze'),
        });
      });

      // Sprawdzamy opony - specjalne traktowanie
      const tiresBefore = this.getTireCondition(table1);
      const tiresAfter = this.getTireCondition(table2);

      if (tiresBefore !== tiresAfter) {
        const isGood = this.isTireConditionBetter(tiresBefore, tiresAfter);
        changes.push({
          what: '🛞 STAN OPON',
          before: tiresBefore,
          after: tiresAfter,
          beforeGood: tiresBefore === 'Dobry',
          afterGood: tiresAfter === 'Dobry',
          isGood: isGood,
          explanation: isGood
            ? 'Opony się poprawiły - świetnie!'
            : 'Opony się zużyły - normalnie przy jeździeniu'
        });
      }

      // Sprawdzamy stan zewnętrzny
      const exteriorBefore = this.getExteriorCondition(table1);
      const exteriorAfter = this.getExteriorCondition(table2);

      if (exteriorBefore !== exteriorAfter) {
        const isGood = exteriorAfter === 'Bez uszkodzeń';
        changes.push({
          what: '✨ BLACHA/LAKIER',
          before: exteriorBefore,
          after: exteriorAfter,
          beforeGood: exteriorBefore === 'Bez uszkodzeń',
          afterGood: exteriorAfter === 'Bez uszkodzeń',
          isGood: isGood,
          explanation: isGood
            ? 'Stan zewnętrzny się poprawił!'
            : 'Pojawiły się nowe uszkodzenia - trzeba sprawdzić'
        });
      }

      // Sprawdzamy czystosc wnetrza
      const interiorBefore = this.getInteriorCondition(table1);
      const interiorAfter = this.getInteriorCondition(table2);

      if (interiorBefore !== interiorAfter) {
        const isGood = interiorAfter === 'Czyste';
        changes.push({
          what: '🧹 WNĘTRZE',
          before: interiorBefore,
          after: interiorAfter,
          beforeGood: interiorBefore === 'Czyste',
          afterGood: interiorAfter === 'Czyste',
          isGood: isGood,
          explanation: isGood
            ? 'Kierowca wyczyścił auto - świetnie!'
            : 'Wnętrze się zabrudziło - normalne użytkowanie'
        });
      }

      // Sprawdzamy stan techniczny (systemy pojazdu)
      const techBefore = this.getTechnicalCondition(table1);
      const techAfter = this.getTechnicalCondition(table2);

      if (techBefore !== techAfter) {
        const isGood = techAfter === 'Sprawne';
        changes.push({
          what: '⚙️ SYSTEMY POJAZDU',
          before: techBefore,
          after: techAfter,
          beforeGood: techBefore === 'Sprawne',
          afterGood: techAfter === 'Sprawne',
          isGood: isGood,
          explanation: isGood
            ? 'Systemy się naprawiły - świetnie!'
            : 'Problemy z systemami - trzeba sprawdzić (światła/wycieraczki/klima/taksometr)'
        });
      }

      // Sprawdzamy inne rzeczy (dokumenty, wyposażenie)
      table1.forEach(item1 => {
        const item2 = table2.find(i => i.name === item1.name);
        if (item2 && item1.value !== item2.value &&
            !item1.name.includes('paliwa') &&
            !/^Poziom (diesla|benzyny|LPG)/.test(item1.name) &&
            !item1.name.includes('opon') &&
            !item1.name.includes('Zewnętrzny') &&
            !item1.name.includes('Wnętrze') &&
            !item1.name.includes('Światła') &&
            !item1.name.includes('wycieraczki') &&
            !item1.name.includes('klima') &&
            !item1.name.includes('taksometr') &&
            !item1.name.includes('terminal')) {
          const isGood = !item1.value && item2.value;
          const itemName = this.getPolishItemName(item1.name);

          changes.push({
            what: itemName,
            before: item1.value ? 'BYŁY' : 'NIE BYŁY',
            after: item2.value ? 'JEST' : 'NIE MA',
            beforeGood: item1.value,
            afterGood: item2.value,
            isGood: isGood,
            explanation: this.getItemExplanation(item1.name, item1.value, item2.value)
          });
        }
      });

      // Sprawdzamy uwagi/opisy
      const descriptionBefore = this.getDescription(0) || '';
      const descriptionAfter = this.getDescription(1) || '';

      if (descriptionBefore !== descriptionAfter && (descriptionBefore || descriptionAfter)) {
        changes.push({
          what: '📝 UWAGI/OPIS',
          before: descriptionBefore || 'Brak uwag',
          after: descriptionAfter || 'Brak uwag',
          beforeGood: true,
          afterGood: true,
          isGood: null, // Neutralne - to tylko informacja
          explanation: 'Zmieniły się uwagi do pojazdu'
        });
      }

      return changes;
    },
    getFuelLevel(table) {
      const fuelItems = table.filter(item => item.name.includes('paliwa'));
      const checkedFuel = fuelItems.find(item => item.value === true);

      if (!checkedFuel) return 'Nieznany';

      if (checkedFuel.name.includes('pełny')) return 'Pełny';
      if (checkedFuel.name.includes('3/4')) return '3/4';
      if (checkedFuel.name.includes('1/2')) return 'Połowa';
      if (checkedFuel.name.includes('1/4')) return '1/4';
      if (checkedFuel.name.includes('rezerwa')) return 'Rezerwa';

      return 'Pusty';
    },
    isFuelLevelBetter(before, after) {
      const levels = ['Pusty', 'Rezerwa', '1/4', 'Połowa', '3/4', 'Pełny'];
      const beforeIndex = levels.indexOf(before);
      const afterIndex = levels.indexOf(after);
      return afterIndex > beforeIndex;
    },
    getPolishItemName(name) {
      const translations = {
        'Dowód rejestracyjny': '📜 DOKUMENTY',
        'Polisa OC / AC': '🛡️ UBEZPIECZENIE',
        'Kluczyki': '🔑 KLUCZYKI',
        'Gaśnica': '🧯 GAŚNICA',
        'Apteczka': '⚕️ APTECZKA',
        'Trójkąt ostrzegawczy': '⚠️ TRÓJKĄT',
        'Kamizelka odblaskowa': '🦺 KAMIZELKA',
        'Koło zapasowe / zestaw naprawczy': '🛑 KOŁO ZAPASOWE',
        'Taksometr': '📱 TAKSOMETR',
        'Kasa fiskalna / terminal płatniczy': '💳 TERMINAL',
        'Zewnętrzny stan: bez uszkodzeń': '✨ BLACHA AUTA',
        'Wnętrze: czyste': '🧹 CZYŚCOŚĆ WNĘTRZA'
      };

      return translations[name] || name.toUpperCase();
    },
    getItemExplanation(name, wasBefore, isNow) {
      if (wasBefore && !isNow) {
        return 'Coś się zepsuło lub zgubiło - trzeba sprawdzić!';
      } else if (!wasBefore && isNow) {
        return 'Kierowca naprawił lub dodał - brawo!';
      }
      return null;
    },
    getTireCondition(table) {
      const tireItems = table.filter(item => item.name.includes('opon'));
      const checkedTire = tireItems.find(item => item.value === true);

      if (!checkedTire) return 'Nieznany';

      if (checkedTire.name.includes('dobry')) return 'Dobry';
      if (checkedTire.name.includes('średni')) return 'Średni';
      if (checkedTire.name.includes('zły')) return 'Zły';

      return 'Nieznany';
    },
    isTireConditionBetter(before, after) {
      const conditions = ['Zły', 'Średni', 'Dobry'];
      const beforeIndex = conditions.indexOf(before);
      const afterIndex = conditions.indexOf(after);
      return afterIndex > beforeIndex;
    },
    getExteriorCondition(table) {
      const exteriorGood = table.find(item => item.name.includes('bez uszkodzeń'));
      const exteriorBad = table.find(item => item.name.includes('zarysowania') || item.name.includes('wgniecenia'));

      if (exteriorGood && exteriorGood.value) return 'Bez uszkodzeń';
      if (exteriorBad && exteriorBad.value) return 'Uszkodzenia';
      return 'Nieznany';
    },
    getInteriorCondition(table) {
      const interiorClean = table.find(item => item.name.includes('czyste'));
      const interiorDirty = table.find(item => item.name.includes('zabrudzone') || item.name.includes('uszkodzenia tapicerki'));

      if (interiorClean && interiorClean.value) return 'Czyste';
      if (interiorDirty && interiorDirty.value) return 'Zabrudzone';
      return 'Nieznany';
    },
    getDescription(protocolIndex) {
      // Pobieramy opis z wybranego protokołu
      if (!this.selectedTables || this.selectedTables.length < 2) return null;

      const protocolId = this.selectedTables[protocolIndex];
      const protocol = this.carHistory.find(item => item.id === protocolId);

      return protocol ? protocol.description : null;
    },
    getTechnicalCondition(table) {
      const techWorking = table.find(item =>
        item.name.includes('Światła') &&
        item.name.includes('sprawne') &&
        item.value
      );
      const techProblems = table.find(item =>
        item.name.includes('Światła') &&
        item.name.includes('uwagi') &&
        item.value
      );

      if (techWorking) return 'Sprawne';
      if (techProblems) return 'Problemy';
      return 'Nieznany';
    },
    async downloadImage(files) {
      for (const { id } of files) {
        const file = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        let blob = new Blob([file.data], { type: file.data.type }),
          url = window.URL.createObjectURL(blob);
        window.open(url);
      }
    },
    async saveStartRent() {
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        this.rent.photos?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Missing Image");
          }
        });
        if (!this.rent.startTime) {
          await this.toggleLoading(false);
          Swal.fire("Brak godziny", "Podaj godzinę wydania auta — potrzebna do mandatów i odpowiedzialności.", "warning");
          return;
        }
        formData.append("startDate", this.chwilaProtokolu(this.rent.startDate, this.rent.startTime));
        formData.append("advance", this.rent.price);
        formData.append("paymentMethod", this.rent.paymentMethod);
        formData.append("description", this.rent.description);
        formData.append("carId", this.currentCarId);
        const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
        const { isEmpty: workerIsEmpty, data: workerData } = this.$refs.workerSignaturePad.saveSignature();
        formData.append("signature", !isEmpty ? data : null);
        formData.append("workerSignature", this.workerFacsimileStart || (!workerIsEmpty ? workerData : null));
        formData.append("driverId", this.rent.driverId);
        formData.append("startMileage", this.rent.startMileage || 0);
        // Nowe zero-jedynkowe metadata
        const fuelMetadata = [];
        if (this.carFuelType === 'DIESEL') {
          fuelMetadata.push({ name: 'Poziom diesla', value: this.fuelLevelDiesel || '' });
        } else if (this.carFuelType === 'BENZYNA') {
          fuelMetadata.push({ name: 'Poziom benzyny', value: this.fuelLevelPetrol || '' });
        } else if (this.carFuelType === 'BENZYNA_LPG') {
          fuelMetadata.push({ name: 'Poziom benzyny', value: this.fuelLevelPetrol || '' });
          fuelMetadata.push({ name: 'Poziom LPG', value: this.fuelLevelLPG || '' });
        }
        const newMetadata = [
          ...fuelMetadata,
          { name: 'fuelLevel', value: this.fuelLevel },
          { name: 'exteriorCondition', value: this.exteriorCondition },
          { name: 'interiorCondition', value: this.interiorCondition },
          { name: 'tireCondition', value: this.tireCondition },
          { name: 'systemsCondition', value: this.systemsCondition },
          // Dodaj stary sposób dla wyposażenia
          ...this.protocol.map((it) => {
            return {
              value: it.checked,
              name: it.label,
            };
          })
        ];

        formData.append("metadata", JSON.stringify(newMetadata));

        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history/start-rent",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.skip = 1;
        await this.toggleLoading(false);
        this.$refs.signaturePad.undoSignature();
        this.$refs.workerSignaturePad.undoSignature();
        this.rent.driverId = null;
        this.rent.description = null;
        this.rent.price = null;
        this.rent.paymentMethod = null;
        this.rent.startDate = null;
        this.rent.startTime = this.godzinaTeraz();
        this.rent.startMileage = null;
        // Resetuj nowe pola
        this.fuelLevel = null;
        this.fuelLevelPetrol = null;
        this.fuelLevelDiesel = null;
        this.fuelLevelLPG = null;
        this.exteriorCondition = null;
        this.interiorCondition = null;
        this.tireCondition = null;
        this.systemsCondition = null;
        this.protocol = this.protocol.map((it) => {
          return {
            checked: false,
            label: it.label,
          };
        });
        this.rent.photos = [];
        this.rentPhotoPreviews.forEach((u) => u && URL.revokeObjectURL(u));
        this.rentPhotoPreviews = [];
        this.dialog = false;
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async fetchDepartments() {
      await this.toggleLoading(true);
      try {
        const departments = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sub-company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.departments = departments.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async initialize() {
      try {
        const dep = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars/" + this.currentCarId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.carFuelType = dep.data.data.fuelType || 'BENZYNA';
        this.currentLicenses = dep.data.data.licenses;
        this.licenses = dep.data.data.licenses.map((dep) => ({
      id: dep.id,
      text: dep.text,
      city:dep.city,
      licenseNo: dep.licenseNo
    }));
        this.driversEndRent = [];
        if (dep.data.data.drivers.length > 0) {
          this.driversEndRent.push({
            id: dep.data.data.drivers[0].id,
            name: `${dep.data.data.drivers[0].firstName} ${dep.data.data.drivers[0].lastName}`,
          });
        }
        if (dep.data.data.secondDrivers.length > 0) {
          this.driversEndRent.push({
            id: dep.data.data.secondDrivers[0].id,
            name: `${dep.data.data.secondDrivers[0].firstName} ${dep.data.data.secondDrivers[0].lastName}`,
          });
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      try {
        if (this.search) {
          this.skip = 1;
        }
        if (this.skip === 1) {
          this.carHistory = [];
        }
        const carHistory = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            "cars-history/" +
            this.currentCarId +
            `?take=10&skip=${this.skip}&search=${this.search}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        if (carHistory.data.data.length === 0) {
          return;
        }
        this.carHistory = this.carHistory
          .concat(
            carHistory.data.data.map((it) => {
              let color = "blue";
              let icon = "mdi-plus";
              switch (it.historyRecordType) {
                case "FORM":
                  color = "green";
                  icon = "mdi-car-key";
                  break;
                case "CRASH":
                  color = "orange";
                  icon = "mdi-tow-truck";
                  break;
                case "SERVICE":
                  color = "black";
                  icon = "mdi-car-cog";
                  break;
                case "MILEAGE":
                  color = "yellow";
                  icon = "mdi-car-speed-limiter";
                  break;
                case "RENT_START":
                  color = "green";
                  icon = "mdi-car-key";
                  break;
                case "RENT_END":
                  color = "deep-orange";
                  icon = "mdi-car-off";
                  break;
              }
              return {
                id: it.id,
                driverId: it.driverId || null,
                // Dla protokołów (RENT_START/END/FORM) preferuj `startDate` ustawioną przez admina,
                // bo `createdAt` to data zapisu w bazie (zwykle "dzisiaj"), a w protokole liczy się
                // data podpisania protokołu wybrana w date-pickerze.
                date: ['FORM', 'RENT_START', 'RENT_END'].includes(it.historyRecordType) && it.startDate
                  ? this.dataProtokolu(it.startDate)
                  : dayjs(it.createdAt).format("YYYY-MM-DD HH:mm"),
                description: it.description,
                type: it.historyRecordType,
                signature: it.signature,
                workerSignature: it.workerSignature,
                metadata:
                  typeof it.metadata === "string"
                    ? JSON.parse(it.metadata)
                    : it.metadata,
                file: it.files.length > 0 ? it.files : null, // Image file object or path
                color,
                icon,
              };
            })
          )
          .flat();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    async getDriversSelect() {
      await this.toggleLoading(true);
      try {
        // take=10000 — w typowej firmie maks ~2000 kierowców, więc 10k to
        // efektywne "wszyscy". Wcześniej był limit 100, przez co większe
        // firmy nie widziały reszty kierowców w protokole najmu.
        const drivers = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `drivers?take=10000&skip=1`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.drivers = drivers.data.data.result.map((it) => {
          return {
            id: it.id,
            name: `${it.firstName} ${it.lastName}`,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async saveLicenses() {
      Swal.fire({
        title: "Napewno zapisać?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "cars-history/licenses/",
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
              data:{
                  carId: this.currentCarId,
                  licenseNo: this.newLicense.licenseNo,
                  city: this.newLicense.city,
                  text: this.newLicense.text,
              },
            });
            this.newLicense.licenseNo = ''
            this.newLicense.city = ''
            this.newLicense.text = ''
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async deleteLicense(id) {
      console.log(id)
      Swal.fire({
        title: "Napewno?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "patch",
              url: process.env.VUE_APP_ROOT_API + "cars-history/licenses/"+id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    getHistoryTypeTitle(type) {
      const titles = {
        'SERVICE': 'Serwis',
        'CRASH': 'Kolizja',
        'MILEAGE': 'Przebieg',
        'FORM': 'Protokół najmu',
        'RENT_START': 'Protokół najmu',
        'RENT_END': 'Protokół zwrotu',
        'OTHER': 'Inne'
      };
      return titles[type] || type;
    },
    // Nazwy kolorów Vuetify pakujemy w hex-e o wysokim kontraście — zamiast surowego
    // "yellow" które przy białym foncie było nieczytelne na pasku zdarzenia historii.
    // headerBg = tło paska, headerFg = kolor tekstu/ikony (z auto-doborem light/dark).
    headerBg(color) {
      const map = {
        yellow: '#F9A825',   // amber darken-3, dobrze widoczny ze stop-tip-em "MILEAGE"
        orange: '#E65100',   // orange darken-4
        red:    '#C62828',
        blue:   '#1565C0',
        green:  '#2E7D32',
        black:  '#263238',
        white:  '#ECEFF1',
        grey:   '#546E7A',
      };
      return map[color] || color || '#546E7A';
    },
    headerFg(color) {
      // Tylko white-background dostaje ciemny tekst; reszta białe dla maksymalnego kontrastu.
      return color === 'white' ? '#212121' : '#FFFFFF';
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return 'Nie podano';
      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN'
      }).format(amount / 100);
    },
    formatNumber(number) {
      if (!number && number !== 0) return '0';
      return new Intl.NumberFormat('pl-PL').format(number);
    },
    async downloadSingleFile(fileId) {
      try {
        const response = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        let blob = new Blob([response.data], { type: response.data.type });
        let url = window.URL.createObjectURL(blob);
        window.open(url);
      } catch (error) {
        console.error('Error downloading file:', error);
        Swal.fire("Błąd", "Nie udało się pobrać pliku", "error");
      }
    },
    // Otwórz PDF protokołu najmu/zwrotu w nowej karcie — backend regeneruje go
    // na żądanie z rekordu CarHistory, więc działa też dla starych zdarzeń bez pliku.
    async downloadRentProtocol(historyId, type) {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars-history/" + historyId + "/protocol-pdf",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        console.error("downloadRentProtocol:", e);
        // responseType:"blob" sprawia że błąd z backendu też wraca jako blob —
        // czytamy go żeby zobaczyć prawdziwą treść błędu.
        let msg = type === 'RENT_END' ? "Nie udało się wygenerować protokołu zwrotu" : "Nie udało się wygenerować protokołu najmu";
        try {
          const blob = e?.response?.data;
          if (blob && typeof blob.text === "function") {
            const text = await blob.text();
            try {
              const parsed = JSON.parse(text);
              msg = parsed?.message || parsed?.error || text || msg;
            } catch {
              if (text) msg = text;
            }
          }
        } catch { /* keep default */ }
        Swal.fire("Błąd protokołu", msg, "error");
      }
    },
  }
};
</script>
<style scoped>
/* Podświetlenie wpisu historii otwartego z powiadomienia */
.history-highlight {
  outline: 2px solid #5478FF;
  box-shadow: 0 0 0 4px rgba(84, 120, 255, 0.25) !important;
}

/* === Driver-app aesthetic for admin FleetDetails === */
.fleet-details-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.fleet-details-page * { box-sizing: border-box; }

/* Page header */
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 4px 18px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #232838;
  border: 1px solid rgba(255,255,255,0.06);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.back-btn:hover { background: #2A3042; border-color: rgba(255,255,255,0.16); }
.back-btn .v-icon { color: inherit !important; }
.page-avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 18px rgba(41, 98, 255, 0.4);
}
.page-eyebrow {
  font-size: 12px;
  color: #6E7691;
  font-weight: 500;
  text-transform: capitalize;
}
.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 2px 0 0;
  letter-spacing: -0.02em;
}

/* === Vuetify deep overrides === */
.fleet-details-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  box-shadow: none !important;
  color: #fff !important;
}
.fleet-details-page >>> .v-card .v-card__title {
  color: #fff !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
  padding: 16px 20px 8px !important;
  word-break: break-word;
}
.fleet-details-page >>> .v-card .v-card__title.text-h4,
.fleet-details-page >>> .v-card .v-card__title.text-h5 {
  font-size: 18px !important;
}
.fleet-details-page >>> .v-card .v-card__title .v-icon { color: #5478FF !important; }
.fleet-details-page >>> .v-card .v-card__title.primary--text { color: #fff !important; }
.fleet-details-page >>> .v-card .v-card__title.warning--text { color: #FFB74D !important; }
.fleet-details-page >>> .v-card .primary--text { color: #5478FF !important; }
.fleet-details-page >>> .v-card .v-card__subtitle { color: #B0BEC5 !important; }
.fleet-details-page >>> .v-card.primary { background: linear-gradient(135deg, #2962FF, #5478FF) !important; }
.fleet-details-page >>> .v-card.primary .v-card__title { color: #fff !important; }

/* Inputs */
.fleet-details-page >>> .v-text-field--outlined fieldset,
.fleet-details-page >>> .v-select--outlined fieldset,
.fleet-details-page >>> .v-textarea--outlined fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}
.fleet-details-page >>> .v-text-field--outlined .v-input__slot,
.fleet-details-page >>> .v-select--outlined .v-input__slot,
.fleet-details-page >>> .v-textarea--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
}
.fleet-details-page >>> .v-label { color: #6E7691 !important; font-size: 13px !important; }
.fleet-details-page >>> .v-text-field input,
.fleet-details-page >>> .v-select__selection,
.fleet-details-page >>> .v-textarea textarea { color: #fff !important; }
.fleet-details-page >>> .v-input__prepend-inner .v-icon,
.fleet-details-page >>> .v-input__append-inner .v-icon { color: #6E7691 !important; }
.fleet-details-page >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #5478FF !important;
}
.fleet-details-page >>> .v-text-field:not(.v-text-field--outlined) input,
.fleet-details-page >>> .v-text-field:not(.v-text-field--outlined) textarea { color: #fff !important; }
.fleet-details-page >>> .v-text-field:not(.v-text-field--outlined) > .v-input__control > .v-input__slot::before {
  border-color: rgba(255,255,255,0.16) !important;
}

/* Data tables */
.fleet-details-page >>> .v-data-table {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 14px !important;
  color: #fff !important;
  box-shadow: none !important;
  margin-bottom: 12px;
}
.fleet-details-page >>> .v-data-table thead tr th {
  background: #1F2434 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.fleet-details-page >>> .v-data-table tbody tr { background: #232838 !important; }
.fleet-details-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.fleet-details-page >>> .v-data-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}
.fleet-details-page >>> .v-data-footer {
  background: #1F2434 !important;
  color: #B0BEC5 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
}

/* Buttons */
.fleet-details-page >>> .v-btn:not(.v-btn--icon):not(.back-btn) {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: -0.01em !important;
  font-weight: 600 !important;
  transition: transform 0.12s ease;
}
.fleet-details-page >>> .v-btn:not(.v-btn--icon):not(.back-btn):hover {
  transform: translateY(-1px);
}

/* Chips */
.fleet-details-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }

/* Dialog cards */
.fleet-details-page >>> .v-dialog .v-card { background: #232838 !important; }

/* Expansion panels */
.fleet-details-page >>> .v-expansion-panel {
  background: #232838 !important;
  border: 1px solid rgba(255,255,255,0.06) !important;
  border-radius: 14px !important;
  margin-bottom: 8px;
  overflow: hidden;
}
.fleet-details-page >>> .v-expansion-panel-header { color: #fff !important; }
.fleet-details-page >>> .v-expansion-panel-content__wrap { color: #B0BEC5 !important; }

/* Lists */
.fleet-details-page >>> .v-list { background: transparent !important; }
.fleet-details-page >>> .v-list-item__title { color: #fff !important; }
.fleet-details-page >>> .v-list-item__subtitle { color: #B0BEC5 !important; }

/* Dividers / progress */
.fleet-details-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.fleet-details-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }

/* Toolbar */
.fleet-details-page >>> .v-toolbar { background: transparent !important; color: #fff !important; }

/* Timeline */
.fleet-details-page >>> .v-timeline-item__body { color: #fff !important; }
.fleet-details-page >>> .v-timeline::before { background: rgba(255,255,255,0.06) !important; }

/* Checkboxes / radio labels */
.fleet-details-page >>> .v-input--checkbox label,
.fleet-details-page >>> .v-input--radio label { color: #B0BEC5 !important; }

/* === Comparison / protocol styles — dark variants === */
.comparison-different {
  background-color: rgba(244, 67, 54, 0.10) !important;
  border-left: 3px solid #FF8A80 !important;
}
.comparison-same {
  background-color: rgba(45, 203, 115, 0.10) !important;
  border-left: 3px solid #6FE89A !important;
}
.comparison-missing {
  background-color: rgba(255, 152, 0, 0.10) !important;
  border-left: 3px solid #FFB74D !important;
}
.protocol-comparison-item {
  border-radius: 10px;
  margin: 4px 0;
  transition: background-color 0.2s ease;
}
.protocol-comparison-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.border-right { border-right: 1px solid rgba(255,255,255,0.06) !important; }
.border-success { border-left: 4px solid #6FE89A !important; }
.border-error   { border-left: 4px solid #FF8A80 !important; }
.border-bottom  { border-bottom: 1px solid rgba(255,255,255,0.06); }

.perfect-condition-card {
  border-radius: 16px !important;
  background: linear-gradient(135deg, rgba(45, 203, 115, 0.10), rgba(45, 203, 115, 0.04)) !important;
  border: 1px solid rgba(45, 203, 115, 0.30) !important;
  box-shadow: 0 8px 24px rgba(45, 203, 115, 0.15) !important;
}
.summary-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.change-category-card {
  border-radius: 14px !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  background: #232838 !important;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.change-category-card:hover {
  border-color: rgba(84, 120, 255, 0.4) !important;
  transform: translateY(-2px);
}
.category-header {
  background: linear-gradient(135deg, #1F2434, #232838) !important;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.change-item { transition: background-color 0.2s ease; }
.change-item:hover { background-color: rgba(255,255,255,0.04); }

.state-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}
.state-chip { min-width: 80px; font-weight: 600; }
.date-info { text-align: center; padding: 8px; color: #B0BEC5; }
.action-summary {
  border-radius: 14px !important;
  border: 2px dashed rgba(255,255,255,0.12) !important;
  background: #1F2434 !important;
}
.changes-container { margin-top: 16px; }
.comparison-side { text-align: center; }
.comparison-side .v-chip { min-width: 100px; font-size: 13px !important; }

/* Description box (before/after texts) */
.description-text {
  background: #181C28;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 12px;
  min-height: 60px;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  max-width: 100%;
  color: #fff;
}
.before-text { border-left: 4px solid #6FE89A; }
.after-text  { border-left: 4px solid #5478FF; }

/* Compare-table colored rows (used in protocols) */
.bg-red    { background-color: rgba(244, 67, 54, 0.20) !important; color: #FF8A80 !important; }
.bg-green  { background-color: rgba(45, 203, 115, 0.20) !important; color: #6FE89A !important; }
.bg-orange { background-color: rgba(255, 152, 0, 0.30) !important; color: #FFB74D !important; }

.protocol-item-name { font-weight: 600; color: #fff; }
.protocol-section {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border-radius: 14px !important;
}
.protocol-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
}
.protocol-section-header {
  background: linear-gradient(135deg, rgba(41, 98, 255, 0.10), rgba(84, 120, 255, 0.06));
  border-radius: 14px 14px 0 0 !important;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.protocol-checkbox {
  border-radius: 10px;
  transition: background-color 0.2s ease;
}
.protocol-checkbox:hover { background-color: rgba(255,255,255,0.04); }

/* Signature pad — keep light because canvas needs white bg for ink */
.signature-container {
  border: 2px dashed rgba(255,255,255,0.18);
  border-radius: 10px;
  padding: 8px;
  background-color: #fff;
}
.signature-pad {
  border-radius: 6px;
  background-color: white;
}

/* Timeline */
.timeline-custom { padding: 20px 0; }
.timeline-card {
  margin-bottom: 12px;
  border-left: 3px solid #5478FF;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.timeline-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
}

.search-field { background-color: #181C28 !important; }
</style>
